import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Checkbox, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { getNestedError, Images } from 'utils';
import { useFormBuilder } from '../../formContext';
import { MentionableInput } from './mentionableInput';
import { useMentionOptions } from './useMentionOptions';
import { TARGET_TOKEN_KEYS } from '../../../hooks/goalModeTable';
import { clearCellHtmlStyle } from '../../../utils/cellTextStyle';
import { ChevronIcon, FieldPicker } from './sessionDataTableElement';
import {
    CELL_TYPES,
    CellTypeIcon,
    cellTypeList,
    getEmptyCellValue,
    getMissingRequiredCells,
    isCellRequired,
    isCellReadOnly,
    getCellMinSelections,
    isOptionCellType,
} from '../../tableCell.constants';
import { countSelections, getEffectiveMinSelections, toMinSelections } from '../../minSelections';

let _rowIdCounter = 0;
const generateRowId = () => `_row_${++_rowIdCounter}_${Date.now()}`;

// The header's column-type dropdown reuses the Session Data Table's field picker.
const columnTypeCatalog = cellTypeList.map((type) => ({ field: type.id, name: type.name }));

/** The table without the transient properties-panel action it was carrying. */
const NO_MISSING_CELLS = new Set();

const RequiredMark = ({ className = 'session-data-required-mark' }) => <span className={className}>*</span>;

/** A focusable target named like the field, for the builders' `scrollToFirstError`. */
const ErrorAnchor = ({ name }) => <div name={name} tabIndex={-1} className="table-required-anchor" />;

const withoutAction = ({ _action, _actionId, _actionPayload, ...rest }) => rest;

/** Whether a row-keyed map key (`${row}_${colId}` or `${row}_${colId}_${side}`) belongs to column `colId`. */
const isColumnKey = (key, colId) => {
    const rest = key.slice(key.indexOf('_') + 1);
    return rest === colId || rest.startsWith(`${colId}_`);
};

/** A row-keyed map without the keys of columns `colIds`. */
const dropColumnKeys = (map, colIds) =>
    Object.fromEntries(Object.entries(map || {}).filter(([key]) => !colIds.some((colId) => isColumnKey(key, colId))));

/**
 * A column type change (the header chevron or right-click on a header): every row of columns
 * `colIds` takes `newType`, so each value is blanked and the columns' per-cell type / option /
 * minimum overrides are dropped. `isLocked(rowIndex, colIndex)` names the cells that must survive
 * it — a Read Only cell while a note is filled: it keeps its value and settings, and its current
 * type (and options) is pinned per cell so the new column type does not reach it. With no locked
 * cell the result is exactly the plain reset. Returns the maps and the body to store.
 */
const resetColumnsForType = ({ table, tableHead, tableBody, colIds, newType, isLocked }) => {
    const lockedKeys = new Set();
    const pinnedTypes = {};
    const pinnedOptions = {};
    colIds.forEach((colId) => {
        const colIndex = tableHead.findIndex((c) => c.id === colId);
        const col = tableHead[colIndex];
        if (!col) return;
        tableBody.forEach((_, rIdx) => {
            if (!isLocked(rIdx, colIndex)) return;
            const key = `${rIdx}_${colId}`;
            lockedKeys.add(key);
            if (!(table.cellTypes || {})[key]) pinnedTypes[key] = col.cellType || CELL_TYPES.TEXT;
            if (!(table.cellOptions || {})[key] && isOptionCellType(col.cellType) && col.options) pinnedOptions[key] = col.options;
        });
    });
    // The column reset, plus the locked cells' own entries (a split cell's halves included).
    const isLockedKey = (key) => lockedKeys.has(key) || [...lockedKeys].some((k) => key.startsWith(`${k}_`));
    const resetMap = (map) => ({
        ...dropColumnKeys(map, colIds),
        ...Object.fromEntries(Object.entries(map || {}).filter(([key]) => isLockedKey(key))),
    });
    const empty = getEmptyCellValue(newType);
    const splitCells = table.splitCells || {};
    const newBody = tableBody.map((row, rIdx) => {
        const newRow = { ...row };
        colIds.forEach((colId) => {
            if (lockedKeys.has(`${rIdx}_${colId}`)) return;
            newRow[colId] = splitCells[`${rIdx}_${colId}`] ? { left: empty, right: empty } : empty;
        });
        return newRow;
    });
    return {
        cellTypes: { ...resetMap(table.cellTypes), ...pinnedTypes },
        cellOptions: { ...resetMap(table.cellOptions), ...pinnedOptions },
        cellMinSelections: resetMap(table.cellMinSelections),
        newBody,
    };
};

/** A row-keyed map with each key's row mapped through `mapRow` (null drops the key). */
const shiftRowKeys = (map, mapRow) => {
    const next = {};
    Object.entries(map || {}).forEach(([key, val]) => {
        const sep = key.indexOf('_');
        const row = mapRow(parseInt(key.slice(0, sep), 10));
        if (row !== null && row !== undefined) next[`${row}${key.slice(sep)}`] = val;
    });
    return next;
};

// Context menu actions
const CONTEXT_ACTIONS = {
    ADD_ROW: 'addRow',
    DELETE_ROW: 'deleteRow',
    ADD_COLUMN: 'addColumn',
    DELETE_COLUMN: 'deleteColumn',
    CHANGE_CELL_TYPE: 'changeCellType',
    MERGE_CELLS: 'mergeCells',
    UNMERGE_CELLS: 'unmergeCells',
    SPLIT_CELL: 'splitCell',
};

// Merge cell helpers
const getMergeKey = (rowIndex, colIndex) => `${rowIndex}_${colIndex}`;

const isCellHidden = (mergedCells, rowIndex, colIndex) => {
    if (!mergedCells) return false;
    for (const merge of Object.values(mergedCells)) {
        if (
            rowIndex >= merge.startRow &&
            rowIndex < merge.startRow + merge.rowSpan &&
            colIndex >= merge.startCol &&
            colIndex < merge.startCol + merge.colSpan &&
            !(rowIndex === merge.startRow && colIndex === merge.startCol)
        ) {
            return true;
        }
    }
    return false;
};

const getMergeForCell = (mergedCells, rowIndex, colIndex) => {
    if (!mergedCells) return null;
    const key = getMergeKey(rowIndex, colIndex);
    return mergedCells[key] || null;
};

const isCellPartOfMerge = (mergedCells, rowIndex, colIndex) => {
    if (!mergedCells) return null;
    for (const merge of Object.values(mergedCells)) {
        if (
            rowIndex >= merge.startRow &&
            rowIndex < merge.startRow + merge.rowSpan &&
            colIndex >= merge.startCol &&
            colIndex < merge.startCol + merge.colSpan
        ) {
            return merge;
        }
    }
    return null;
};

// --- ContentEditable cell that preserves cursor position ---
const ContentEditableCell = React.memo(({ initialValue, readOnly, onChange }) => {
    const elRef = useRef(null);
    const initialValueRef = useRef(initialValue);

    useEffect(() => {
        if (elRef.current && initialValueRef.current !== undefined) {
            elRef.current.innerHTML = initialValueRef.current;
        }
    }, []);

    return (
        <div
            ref={elRef}
            className="table-cell-html-content"
            contentEditable={!readOnly}
            suppressContentEditableWarning
            onBlur={(e) => onChange(e.target.innerHTML)}
            style={{ wordBreak: 'break-word', fontSize: 'inherit', lineHeight: '19px', color: 'inherit', fontWeight: 'inherit', fontStyle: 'inherit', textDecoration: 'inherit', outline: 'none', cursor: 'text' }}
        />
    );
});

// Gap kept between a context menu and the window edge.
const MENU_VIEWPORT_MARGIN = 8;

/**
 * Where a `size`-long menu opened at `at` starts so it stays in a `viewport`-long window: at `at` when it fits,
 * else ending at `at` (opened up / left of the cursor), else pinned to the window's far edge.
 */
const fitInViewport = (at, size, viewport) => {
    const max = viewport - MENU_VIEWPORT_MARGIN;
    if (at + size <= max) return at;
    if (at - size >= MENU_VIEWPORT_MARGIN) return at - size;
    return Math.max(MENU_VIEWPORT_MARGIN, max - size);
};

// --- Context Menu Component ---
const TableContextMenu = ({
    position,
    onClose,
    onAction,
    cellType,
    canMerge,
    isMergedCell,
    canSplit,
    selectedCells = [],
    rowsLocked = false,
    // In a note, every cell this would change is Read Only: its type cannot be changed.
    cellTypeLocked = false,
}) => {
    const menuRef = useRef(null);
    const [showCellTypeSubmenu, setShowCellTypeSubmenu] = useState(false);
    const onCloseRef = useRef(onClose);
    useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                onCloseRef.current();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Keep the menu inside the window: a right-click near the bottom / right edge opens it up / left of the
    // cursor. Measured before paint (`offsetHeight` / `offsetWidth` ignore the fade-in's scale), so it never
    // shows cut off first. The menu stays inside the table's DOM (no portal): the table clears its cell
    // selection on a mousedown outside itself, which would empty the cells a menu action works on.
    // The progress report builder zooms the report with CSS `zoom`, which also scales a fixed element's
    // `top` / `left` and its size, while the cursor's position is in screen pixels: the fit is worked out in
    // screen pixels and divided back by the menu's zoom (`currentCSSZoom`; 1 where the browser lacks it).
    const [menuPos, setMenuPos] = useState({ top: position.y, left: position.x });
    useLayoutEffect(() => {
        const el = menuRef.current;
        if (!el) return;
        const zoom = el.currentCSSZoom || 1;
        setMenuPos({
            top: fitInViewport(position.y, el.offsetHeight * zoom, window.innerHeight) / zoom,
            left: fitInViewport(position.x, el.offsetWidth * zoom, window.innerWidth) / zoom,
        });
    }, [position.x, position.y]);

    // The Change Cell Type submenu likewise: moved up when it would run past the bottom, opened to the left
    // when it would run past the right edge.
    const submenuRef = useRef(null);
    const [submenuStyle, setSubmenuStyle] = useState(null);
    useLayoutEffect(() => {
        const el = submenuRef.current;
        if (!showCellTypeSubmenu || !el) {
            setSubmenuStyle(null);
            return;
        }
        // Screen pixels (see the menu above): the anchor's rect is on screen, sizes and `top` are scaled by the zoom.
        const zoom = el.currentCSSZoom || 1;
        const anchor = el.parentElement.getBoundingClientRect();
        const height = el.offsetHeight * zoom;
        const style = {};
        const maxBottom = window.innerHeight - MENU_VIEWPORT_MARGIN;
        // `.table-context-submenu { top: -4px }`: shifted up just enough to end at the window's bottom, never above its top.
        if (anchor.top - 4 * zoom + height > maxBottom) {
            style.top = (Math.max(MENU_VIEWPORT_MARGIN, maxBottom - height) - anchor.top) / zoom;
        }
        if (anchor.right + el.offsetWidth * zoom > window.innerWidth - MENU_VIEWPORT_MARGIN) {
            style.left = 'auto';
            style.right = '100%';
        }
        setSubmenuStyle(style);
    }, [showCellTypeSubmenu]);

    return (
        <div ref={menuRef} className="table-context-menu" style={menuPos}>
            {/* Header */}
            <div className="table-context-menu-header">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0C66E4" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M3 9h18" />
                    <path d="M9 3v18" />
                </svg>
                <span>Cell Actions</span>
            </div>
            <div className="table-context-menu-divider" />

            {!rowsLocked && (
                <button type="button" className="table-context-menu-item" onClick={() => onAction(CONTEXT_ACTIONS.ADD_ROW)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0C66E4" strokeWidth="2.5">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    <span>Add Row</span>
                </button>
            )}
            <button type="button" className="table-context-menu-item" onClick={() => onAction(CONTEXT_ACTIONS.ADD_COLUMN)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0C66E4" strokeWidth="2.5">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span>Add Column</span>
            </button>
            <button type="button" className="table-context-menu-item delete" onClick={() => onAction(CONTEXT_ACTIONS.DELETE_ROW)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D92D20" strokeWidth="2">
                    <path d="M3 6h18" />
                    <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                </svg>
                <span>
                    {new Set(selectedCells.filter((c) => c.rowIndex >= 0).map((c) => c.rowIndex)).size > 1 ? 'Delete Rows' : 'Delete Row'}
                </span>
            </button>
            <button type="button" className="table-context-menu-item delete" onClick={() => onAction(CONTEXT_ACTIONS.DELETE_COLUMN)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D92D20" strokeWidth="2">
                    <path d="M3 6h18" />
                    <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                </svg>
                <span>{new Set(selectedCells.map((c) => c.colIndex)).size > 1 ? 'Delete Columns' : 'Delete Column'}</span>
            </button>
            <div className="table-context-menu-divider" />

            {/* Change Cell Type with submenu */}
            <div
                className={`table-context-menu-item has-submenu${cellTypeLocked ? ' disabled' : ''}`}
                title={cellTypeLocked ? 'Read Only cell: its type cannot be changed' : undefined}
                onMouseEnter={() => !cellTypeLocked && setShowCellTypeSubmenu(true)}
                onMouseLeave={() => setShowCellTypeSubmenu(false)}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#344054" strokeWidth="2" strokeLinecap="round">
                    <path d="M4 7V4h16v3" />
                    <path d="M12 4v16" />
                    <path d="M8 20h8" />
                </svg>
                <span>Change Cell Type</span>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#344054" strokeWidth="2" style={{ marginLeft: 'auto' }}>
                    <polyline points="9 18 15 12 9 6" />
                </svg>
                {showCellTypeSubmenu && (
                    <div ref={submenuRef} className="table-context-submenu" style={submenuStyle || undefined}>
                        {cellTypeList.map((ct) => {
                            const isActive = cellType === ct.id;
                            return (
                                <button
                                    type="button"
                                    key={ct.id}
                                    className={`table-context-menu-item ${isActive ? 'active' : ''}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onAction(CONTEXT_ACTIONS.CHANGE_CELL_TYPE, ct.id);
                                    }}
                                >
                                    <CellTypeIcon type={ct.id} color={isActive ? '#0C66E4' : '#344054'} size={16} />
                                    <span>{ct.name}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="table-context-menu-divider" />

            {/* Merge/Unmerge/Split */}
            <button
                type="button"
                className={`table-context-menu-item ${!canMerge ? 'disabled' : ''}`}
                disabled={!canMerge}
                onClick={() => onAction(CONTEXT_ACTIONS.MERGE_CELLS)}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={canMerge ? '#344054' : '#98A2B3'} strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M3 12h18" />
                    <path d="M12 3v18" />
                </svg>
                <span>Merge Cells</span>
            </button>
            <button
                type="button"
                className={`table-context-menu-item ${!isMergedCell ? 'disabled' : ''}`}
                disabled={!isMergedCell}
                onClick={() => onAction(CONTEXT_ACTIONS.UNMERGE_CELLS)}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isMergedCell ? '#344054' : '#98A2B3'} strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M3 9h18" />
                    <path d="M3 15h18" />
                    <path d="M9 3v18" />
                </svg>
                <span>Unmerge Cells</span>
            </button>
            <button
                type="button"
                className={`table-context-menu-item ${!canSplit ? 'disabled' : ''}`}
                disabled={!canSplit}
                onClick={() => onAction(CONTEXT_ACTIONS.SPLIT_CELL)}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={canSplit ? '#344054' : '#98A2B3'} strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M12 3v18" />
                </svg>
                <span>Split Cell</span>
            </button>
        </div>
    );
};

// --- Select / Multi-Select Dropdown Component ---
// Custom dropdown (not a native <select>) so long option names wrap instead of being cut off.
// `single` = one value: picking an option replaces it and closes; picking it again clears it.
const TableMultiSelect = ({ options, selected, onChange, readOnly, single = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
    const wrapperRef = useRef(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e) => {
            if (
                wrapperRef.current && !wrapperRef.current.contains(e.target) &&
                dropdownRef.current && !dropdownRef.current.contains(e.target)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const toggleOption = (opt) => {
        const isSelected = selected.some((s) => (s?.id || s) === opt.id);
        if (single) {
            onChange(isSelected ? null : { name: opt.name, id: opt.id });
            setIsOpen(false);
            return;
        }
        const updated = isSelected
            ? selected.filter((s) => (s?.id || s) !== opt.id)
            : [...selected, { name: opt.name, id: opt.id }];
        onChange(updated);
    };

    const handleOpen = () => {
        if (readOnly) return;
        if (!isOpen && wrapperRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect();
            setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 160) });
        }
        setIsOpen(!isOpen);
    };

    return (
        <div className="table-cell-multi-select" ref={wrapperRef}>
            <div
                className={`table-cell-multi-select-trigger ${readOnly ? 'disabled' : ''}`}
                onClick={handleOpen}
            >
                {selected.length > 0 ? (
                    <div className="table-cell-multi-select-tags">
                        {selected.map((s) => (
                            <span key={s?.id || s} className="table-cell-multi-select-tag">
                                {s?.name || s}
                                {!readOnly && !single && (
                                    <span
                                        className="table-cell-multi-select-tag-remove"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleOption(s);
                                        }}
                                    >
                                        ×
                                    </span>
                                )}
                            </span>
                        ))}
                    </div>
                ) : (
                    <span className="table-cell-multi-select-placeholder">...</span>
                )}
                {!readOnly && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#667085" strokeWidth="2" style={{ flexShrink: 0 }}>
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                )}
            </div>
            {isOpen && (
                <div
                    ref={dropdownRef}
                    className="table-cell-multi-select-dropdown"
                    style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
                >
                    {options.map((opt) => {
                        const isChecked = selected.some((s) => (s?.id || s) === opt.id);
                        return (
                            <label
                                key={opt.id}
                                className={`table-cell-multi-select-option ${isChecked ? 'checked' : ''} ${single ? 'single' : ''}`}
                                onClick={single ? () => toggleOption(opt) : undefined}
                            >
                                {!single && (
                                    <Checkbox
                                        checked={isChecked}
                                        onChange={() => toggleOption(opt)}
                                        size="small"
                                        style={{ width: '38px', flexShrink: 0, color: isChecked ? '#0C66E4' : undefined }}
                                    />
                                )}
                                <span className="table-cell-multi-select-option-label">{opt.name}</span>
                            </label>
                        );
                    })}
                    {options.length === 0 && (
                        <div className="table-cell-multi-select-empty">No options</div>
                    )}
                </div>
            )}
        </div>
    );
};

// --- Cell Renderer ---
const TableCellContent = ({
    cellType,
    cellValue,
    colOptions = [],
    optionsLayout = 'vertical',
    onChange,
    readOnly,
    isFillerMode,
    mentionOptions,
    onMentionInsert,
    controlRegister,
    registerName,
    control,
}) => {
    const type = cellType || CELL_TYPES.TEXT;

    if (type === CELL_TYPES.CHECKBOX) {
        return (
            <div className="table-cell-checkbox">
                <Checkbox
                    checked={!!cellValue}
                    onChange={(e) => onChange(e.target.checked)}
                    disabled={readOnly}
                    style={{ padding: 0, width: 'auto' }}
                    size="small"
                />
            </div>
        );
    }

    if (type === CELL_TYPES.RADIO) {
        const currentId = cellValue?.id || '';
        return (
            <div className="table-cell-radio" style={{ gap: 2 }}>
                <RadioGroup
                    value={currentId}
                    onChange={(e) => {
                        const selectedId = e.target.value;
                        const selectedOption = (colOptions || []).find((opt) => opt.id === selectedId);
                        onChange(selectedOption ? { name: selectedOption.name, id: selectedOption.id } : null);
                    }}
                >
                    {(colOptions || []).map((opt) => (
                        <FormControlLabel
                            key={opt.id}
                            value={opt.id}
                            disabled={readOnly}
                            control={<Radio size="small" style={{ padding: '0 6px', width: 'auto' }} />}
                            label={opt.name}
                            style={{ marginRight: 0 }}
                            componentsProps={{ typography: { style: { fontSize: 'inherit', color: 'inherit', fontWeight: 'inherit', fontStyle: 'inherit', textDecoration: 'inherit' } } }}
                        />
                    ))}
                </RadioGroup>
            </div>
        );
    }

    if (type === CELL_TYPES.CHECKBOX_GROUP) {
        const selected = Array.isArray(cellValue) ? cellValue : [];
        const isHorizontal = optionsLayout === 'horizontal';
        return (
            <div
                className="table-cell-checkbox-group"
                style={{ display: 'flex', flexDirection: isHorizontal ? 'row' : 'column', flexWrap: 'wrap', gap: isHorizontal ? 8 : 2 }}
            >
                {(colOptions || []).map((opt) => {
                    const isChecked = selected.some((s) => (s?.id || s) === opt.id || (s?.name || s) === opt.name);
                    return (
                        <FormControlLabel
                            key={opt.id}
                            disabled={readOnly}
                            control={
                                <Checkbox
                                    checked={isChecked}
                                    onChange={() => {
                                        const updated = isChecked
                                            ? selected.filter((s) => (s?.id || s) !== opt.id && (s?.name || s) !== opt.name)
                                            : [...selected, { name: opt.name, id: opt.id }];
                                        onChange(updated);
                                    }}
                                    size="small"
                                    style={{ padding: '0 6px', width: 'auto' }}
                                />
                            }
                            label={opt.name}
                            style={{ marginRight: 0 }}
                            componentsProps={{ typography: { style: { fontSize: 'inherit', color: 'inherit', fontWeight: 'inherit', fontStyle: 'inherit', textDecoration: 'inherit' } } }}
                        />
                    );
                })}
            </div>
        );
    }

    if (type === CELL_TYPES.SELECT) {
        return (
            <TableMultiSelect
                single
                options={colOptions || []}
                selected={cellValue?.id ? [cellValue] : []}
                onChange={onChange}
                readOnly={readOnly}
            />
        );
    }

    if (type === CELL_TYPES.MULTI_SELECT) {
        const selected = Array.isArray(cellValue) ? cellValue : [];
        return (
            <TableMultiSelect
                options={colOptions || []}
                selected={selected}
                onChange={onChange}
                readOnly={readOnly}
            />
        );
    }

    // Default: TEXT
    if (isFillerMode) {
        const hasHtml = typeof cellValue === 'string' && (cellValue.includes('<') || cellValue.includes('&'));
        if (hasHtml) {
            return <ContentEditableCell initialValue={cellValue} readOnly={readOnly} onChange={onChange} />;
        }
        return (
            <textarea
                className="form-input-box"
                defaultValue={cellValue || ''}
                placeholder="..."
                disabled={readOnly}
                rows={1}
                ref={(el) => {
                    if (el) {
                        requestAnimationFrame(() => {
                            el.style.height = 'auto';
                            el.style.height = el.scrollHeight + 'px';
                        });
                    }
                }}
                onChange={(e) => {
                    onChange(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                }}
                onInput={(e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                }}
                style={{
                    width: '100%',
                    border: 'none',
                    background: 'transparent',
                    resize: 'none',
                    overflow: 'hidden',
                    wordBreak: 'break-word',
                    fontSize: 'inherit',
                    padding: 0,
                    color: 'inherit',
                    fontWeight: 'inherit',
                    fontStyle: 'inherit',
                    textDecoration: 'inherit',
                    lineHeight: '19px',
                    outline: 'none',
                    height: 'auto',
                }}
            />
        );
    }

    // Builder mode: mentionable input
    return (
        <div className="mention-wrapper table-mention-wrapper" style={{ fontSize: 'inherit', color: 'inherit', fontWeight: 'inherit', fontStyle: 'inherit', textDecoration: 'inherit' }}>
            <MentionableInput
                className="mention-input"
                defaultValue={cellValue || ''}
                onChange={(html) => onChange(html)}
                readOnly={readOnly}
                mentionOptions={mentionOptions || []}
                onMentionInsert={onMentionInsert}
                noError={true}
            />
        </div>
    );
};

// --- Main Component ---
export const TableElement = ({
    item,
    readOnly,
    index,
    control,
    name,
    watch,
    setValue: setValueProp,
    noMentions,
    marginBottom,
    wrapperStyle = {},
    // When provided, structural + value edits (builder mode) are persisted
    // through this callback instead of the FormBuilder context. The report
    // builder passes this so a table can be fully edited (add/delete
    // rows & columns, cell types, merge/split) even though it renders every
    // section at once — where the context's `sections[activeSection-1]`
    // addressing would target the wrong attribute.
    onUpdate,
    // The form's root errors, for marking empty required cells.
    errors,
    // Report-builder fill surface: the form's control, used only to register
    // the required-cells rule on `name` (there is no `control` in that mode —
    // the table is edited in builder mode and persisted through `onUpdate`).
    validationControl,
    // A note being filled in builder mode (report builder): Read Only cells are locked as in
    // filler mode. Left off in the template builder, where the author types the cell's value.
    lockReadOnlyCells,
    // With `onUpdate` (report builder): told the clicked cell, which is not stored on the table there.
    onCellSelect,
}) => {
    const { formList, setFormList, activeSection } = useFormBuilder();
    const goalMode = !!item?.properties?.goalMode;
    const targetMode = !!item?.properties?.targetMode;
    // Template builder: a Goal Mode table offers the target tokens even with Target Mode off,
    // and picking one turns Target Mode on in the same update as the cell (`updateTableCallback`).
    // A target token only resolves on a row per target, and hiding them left authors typing
    // `@TargetObjective` that never became a chip.
    const canEnableTargetMode = goalMode && !targetMode && !control && !onUpdate && !readOnly;
    const enableTargetModeRef = useRef(false);
    const handleMentionInsert = canEnableTargetMode
        ? (option) => {
              if (TARGET_TOKEN_KEYS.includes(option?.defaultValue)) enableTargetModeRef.current = true;
          }
        : undefined;
    const currentModelEnums = useMentionOptions({
        noMentions,
        activeSection,
        goalMode,
        targetMode: targetMode || canEnableTargetMode,
    });
    const table = item?.properties?.table || { tableHead: [] };
    const tableHead = table.tableHead || [];
    const showHeader = table.showHeader !== false;
    const showBorders = table.showBorders !== false;
    const tableBody = Array.isArray(item?.value) ? item.value.map((row) => (row?._rowId ? row : { ...row, _rowId: generateRowId() })) : [];

    /**
     * Required cells block saving the note: one rule on the table's value
     * checks every required cell. It reads the layout through a ref so the rule
     * registered once always sees the current table.
     */
    const validationRef = useRef({ table, readOnly });
    validationRef.current = { table, readOnly };
    const requiredRule = useMemo(
        () => ({
            validate: (rows) => {
                const { table: current, readOnly: locked } = validationRef.current;
                if (locked) return true;
                return getMissingRequiredCells(current, Array.isArray(rows) ? rows : []).size === 0 || 'Required';
            },
        }),
        []
    );

    const { fields, append, remove, move } = control
        ? useFieldArray({ control, name, rules: requiredRule })
        : { fields: [], append: () => {}, remove: () => {}, move: () => {} };

    const [contextMenu, setContextMenu] = useState(null);
    const [typePicker, setTypePicker] = useState(null);
    const closeTypePicker = useCallback(() => setTypePicker(null), []);
    const [selectedCell, setSelectedCell] = useState(null);
    const [selectedCells, setSelectedCells] = useState([]);
    const selectedCellsRef = useRef([]);
    const tableWrapperRef = useRef(null);
    // Keep ref in sync with state
    useEffect(() => {
        selectedCellsRef.current = selectedCells;
    }, [selectedCells]);
    const mergedCells = table.mergedCells || {};

    const isFillerMode = !!control;
    // A note being filled (filler mode or `lockReadOnlyCells`): a Read Only body cell keeps its type
    // and value — Change Cell Type and a column type change skip it. The template builder changes
    // it like any cell.
    const isTypeLockedCell = (rowIndex, colIndex) =>
        (isFillerMode || !!lockReadOnlyCells) && rowIndex >= 0 && !!tableHead[colIndex] && isCellReadOnly(table, tableHead[colIndex], rowIndex);
    const rowsToRender = isFillerMode ? fields : tableBody;
    // Goal Mode: the template's single row is repeated once per goal when the
    // form is filled (see `hooks/goalModeTable.js`), so rows are never added by
    // hand and the last row can never be deleted.
    const rowsLocked = !!item?.properties?.goalMode;
    const lastActionIdRef = useRef(null);

    const tableError = errors && name ? getNestedError(errors, name) : null;
    const missingCells = tableError
        ? getMissingRequiredCells(table, isFillerMode && watch ? watch(name) || [] : tableBody)
        : NO_MISSING_CELLS;

    // --- Click outside to deselect and close context menu ---
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (tableWrapperRef.current && !tableWrapperRef.current.contains(e.target)) {
                // Don't clear selection when clicking on the properties panel
                const isPropertiesPanel =
                    e.target.closest('.table-properties-panel') ||
                    e.target.closest('.table-props-section') ||
                    e.target.closest('.properties-section');
                if (isPropertiesPanel) return;

                setContextMenu(null);
                setSelectedCell(null);
                setSelectedCells([]);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // --- Update helpers ---
    const updateTableCallback = useCallback(
        (newTableProperties, newValue) => {
            // Report-builder path: caller owns persistence (writes to the exact
            // RHF path for this attribute, wherever it is nested).
            if (onUpdate) {
                onUpdate(newTableProperties, newValue);
                return;
            }
            if (!setFormList) return;
            const newFormList = { ...formList };
            const currentSectionIndex = (activeSection || 1) - 1;
            const currentSection = newFormList?.sections?.[currentSectionIndex];
            if (!currentSection?.attributes) return;
            const newAttributes = [...currentSection.attributes];
            if (!newAttributes[index]) return;

            const enableTargetMode = enableTargetModeRef.current;
            enableTargetModeRef.current = false;
            newAttributes[index] = {
                ...newAttributes[index],
                value: newValue !== undefined ? newValue : newAttributes[index].value,
                properties: {
                    ...(newAttributes[index].properties || {}),
                    table: newTableProperties !== undefined ? newTableProperties : newAttributes[index]?.properties?.table,
                    ...(enableTargetMode ? { targetMode: true } : {}),
                },
            };

            newFormList.sections[currentSectionIndex].attributes = newAttributes;
            setFormList(newFormList);
        },
        [onUpdate, formList, setFormList, activeSection, index]
    );

    // Latest committed body for builder-mode row ops. In onUpdate mode the value
    // lives at the RHF path (`name`), which is the single source of truth and is
    // re-read fresh here to avoid stale-closure overwrites on rapid edits. In
    // context mode it comes from formList (unchanged behavior).
    const readCurrentBody = useCallback(() => {
        if (onUpdate) {
            const v = watch && name ? watch(name) : item?.value;
            return Array.isArray(v) ? v : [];
        }
        const currentSectionIndex = (activeSection || 1) - 1;
        const currentValue = formList?.sections?.[currentSectionIndex]?.attributes?.[index]?.value;
        return Array.isArray(currentValue) ? currentValue : [];
    }, [onUpdate, watch, name, item, formList, activeSection, index]);

    const getKey = (col) => col.id;

    // --- Row operations ---
    const handleAddRow = useCallback(() => {
        if (readOnly || rowsLocked) return;
        const newRow = { _rowId: generateRowId() };
        tableHead.forEach((col) => {
            newRow[getKey(col)] = '';
        });
        if (isFillerMode) {
            append(newRow);
        } else {
            // Read latest committed body to avoid stale closure
            const currentBody = readCurrentBody();
            updateTableCallback(undefined, [...currentBody, newRow]);
        }
    }, [readOnly, rowsLocked, tableHead, isFillerMode, append, updateTableCallback, readCurrentBody]);

    const handleRemoveRow = useCallback(
        (rowIndex) => {
            if (readOnly) return;
            if (rowsLocked && rowsToRender.length <= 1) return;
            if (isFillerMode) {
                remove(rowIndex);
            } else {
                // Read latest committed body to avoid stale closure
                const currentBody = readCurrentBody();
                const newBody = currentBody.filter((_, i) => i !== rowIndex);

                // Clean up mergedCells: remove merges involving deleted row, shift rows after it
                const newMergedCells = {};
                for (const [key, merge] of Object.entries(mergedCells)) {
                    if (merge.startRow === -1) {
                        // Header merges are unaffected by body row deletion
                        newMergedCells[key] = merge;
                        continue;
                    }
                    if (rowIndex >= merge.startRow && rowIndex < merge.startRow + merge.rowSpan) {
                        // Row is inside this merge
                        if (merge.rowSpan > 1) {
                            const newRowSpan = merge.rowSpan - 1;
                            const newStartRow = rowIndex === merge.startRow ? merge.startRow : merge.startRow;
                            const shiftedStart = newStartRow > rowIndex ? newStartRow - 1 : newStartRow;
                            newMergedCells[getMergeKey(shiftedStart, merge.startCol)] = {
                                ...merge,
                                startRow: shiftedStart,
                                rowSpan: newRowSpan,
                            };
                        }
                        // If rowSpan was 1, just remove the merge entirely
                    } else {
                        const shiftedStart = merge.startRow > rowIndex ? merge.startRow - 1 : merge.startRow;
                        newMergedCells[getMergeKey(shiftedStart, merge.startCol)] = {
                            ...merge,
                            startRow: shiftedStart,
                        };
                    }
                }

                // Clean up cellTypes: remove entries for deleted row, shift rows after it
                const oldCellTypes = table.cellTypes || {};
                const newCellTypes = {};
                for (const [cellKey, type] of Object.entries(oldCellTypes)) {
                    // Keys can be "rowIndex_colId" or "rowIndex_colId_side"
                    const firstUnderscore = cellKey.indexOf('_');
                    const rStr = cellKey.substring(0, firstUnderscore);
                    const rest = cellKey.substring(firstUnderscore + 1);
                    const r = parseInt(rStr, 10);
                    if (r === rowIndex) continue;
                    const newR = r > rowIndex ? r - 1 : r;
                    newCellTypes[`${newR}_${rest}`] = type;
                }

                // Clean up splitCells: remove entries for deleted row, shift rows after it
                const oldSplitCells = table.splitCells || {};
                const newSplitCells = {};
                for (const [cellKey, val] of Object.entries(oldSplitCells)) {
                    const firstUnderscore = cellKey.indexOf('_');
                    const rStr = cellKey.substring(0, firstUnderscore);
                    const rest = cellKey.substring(firstUnderscore + 1);
                    const r = parseInt(rStr, 10);
                    if (r === rowIndex) continue;
                    const newR = r > rowIndex ? r - 1 : r;
                    newSplitCells[`${newR}_${rest}`] = val;
                }

                // Clean up cellOptions: remove entries for deleted row, shift rows after it
                const oldCellOptions = table.cellOptions || {};
                const newCellOptions = {};
                for (const [cellKey, opts] of Object.entries(oldCellOptions)) {
                    const firstUnderscore = cellKey.indexOf('_');
                    const rStr = cellKey.substring(0, firstUnderscore);
                    const rest = cellKey.substring(firstUnderscore + 1);
                    const r = parseInt(rStr, 10);
                    if (r === rowIndex) continue;
                    const newR = r > rowIndex ? r - 1 : r;
                    newCellOptions[`${newR}_${rest}`] = opts;
                }

                updateTableCallback(
                    {
                        ...table,
                        mergedCells: newMergedCells,
                        cellTypes: newCellTypes,
                        splitCells: newSplitCells,
                        cellOptions: newCellOptions,
                        cellRequired: shiftRowKeys(table.cellRequired, (r) => (r === rowIndex ? null : r > rowIndex ? r - 1 : r)),
                        cellMinSelections: shiftRowKeys(table.cellMinSelections, (r) => (r === rowIndex ? null : r > rowIndex ? r - 1 : r)),
                        cellReadOnly: shiftRowKeys(table.cellReadOnly, (r) => (r === rowIndex ? null : r > rowIndex ? r - 1 : r)),
                    },
                    newBody
                );
            }
        },
        [readOnly, rowsLocked, rowsToRender, isFillerMode, remove, table, mergedCells, updateTableCallback, readCurrentBody]
    );

    // --- Column operations ---
    const handleAddColumn = useCallback(() => {
        if (readOnly) return;
        const newCol = { id: uuidv4(), name: `Column ${tableHead.length + 1}`, cellType: CELL_TYPES.TEXT };
        const newHead = [...tableHead, newCol];
        const newBody = tableBody.map((row) => ({ ...row, [newCol.id]: '' }));
        updateTableCallback({ ...withoutAction(table), tableHead: newHead }, newBody);
    }, [readOnly, tableHead, tableBody, table, updateTableCallback]);

    /**
     * Patch one column's header properties (`name`, `cellType`, `options`).
     * The column type is the default for every row, so changing it drops the
     * column's per-cell type/option overrides and blanks its values — every row
     * of the column then takes the new type. Cells can still be overridden
     * afterwards from CELL TYPES / the context menu.
     */
    const handleUpdateColumn = useCallback(
        (colId, patch) => {
            if (readOnly) return;
            const col = tableHead.find((c) => c.id === colId);
            if (!col) return;

            const nextCol = { ...col, ...patch };
            const newHead = tableHead.map((c) => (c.id === colId ? nextCol : c));
            // Like the type, the column's `required` and `minSelections` are the
            // defaults for every row: setting one drops the column's per-cell overrides of it.
            const requiredPatch = {
                ...(patch.required !== undefined ? { cellRequired: dropColumnKeys(table.cellRequired, [colId]) } : {}),
                ...(patch.minSelections !== undefined ? { cellMinSelections: dropColumnKeys(table.cellMinSelections, [colId]) } : {}),
            };
            const typeChanged = patch.cellType !== undefined && patch.cellType !== (col.cellType || CELL_TYPES.TEXT);
            if (!typeChanged) {
                updateTableCallback({ ...withoutAction(table), ...requiredPatch, tableHead: newHead }, undefined);
                return;
            }

            // Minimum selections only means something on a multi-choice column;
            // don't leave a hidden one behind when the type changes away from it.
            if (nextCol.cellType !== CELL_TYPES.CHECKBOX_GROUP && nextCol.cellType !== CELL_TYPES.MULTI_SELECT) {
                delete nextCol.minSelections;
            }
            if (isOptionCellType(nextCol.cellType) && !nextCol.options?.length) {
                nextCol.options = [{ id: uuidv4(), name: 'Option 1' }];
            }
            // Read Only cells of a note being filled keep their value and type (`resetColumnsForType`).
            const { newBody, ...resetMaps } = resetColumnsForType({
                table,
                tableHead,
                tableBody,
                colIds: [colId],
                newType: nextCol.cellType,
                isLocked: isTypeLockedCell,
            });

            updateTableCallback({ ...withoutAction(table), ...requiredPatch, tableHead: newHead, ...resetMaps }, newBody);
        },
        [readOnly, tableHead, tableBody, table, updateTableCallback, isFillerMode, lockReadOnlyCells]
    );

    const handleRemoveColumn = useCallback(
        (colId) => {
            if (readOnly) return;
            const colIndex = tableHead.findIndex((col) => col.id === colId);
            const newHead = tableHead.filter((col) => col.id !== colId);
            const newBody = tableBody.map((row) => {
                const newRow = { ...row };
                delete newRow[colId];
                return newRow;
            });

            // Clean up mergedCells: remove or adjust merges involving the deleted column
            const newMergedCells = {};
            for (const [key, merge] of Object.entries(mergedCells)) {
                if (colIndex >= merge.startCol && colIndex < merge.startCol + merge.colSpan) {
                    if (merge.colSpan > 1) {
                        const newColSpan = merge.colSpan - 1;
                        const shiftedStart = merge.startCol > colIndex ? merge.startCol - 1 : merge.startCol;
                        newMergedCells[getMergeKey(merge.startRow, shiftedStart)] = {
                            ...merge,
                            startCol: shiftedStart,
                            colSpan: newColSpan,
                        };
                    }
                } else {
                    const shiftedStart = merge.startCol > colIndex ? merge.startCol - 1 : merge.startCol;
                    newMergedCells[getMergeKey(merge.startRow, shiftedStart)] = {
                        ...merge,
                        startCol: shiftedStart,
                    };
                }
            }

            // Clean up cellTypes: remove entries for deleted column
            const oldCellTypes = table.cellTypes || {};
            const newCellTypes = {};
            for (const [cellKey, type] of Object.entries(oldCellTypes)) {
                if (cellKey.includes(`_${colId}`) || cellKey.includes(`_${colId}_`)) continue;
                newCellTypes[cellKey] = type;
            }

            // Clean up splitCells: remove entries for deleted column
            const oldSplitCells = table.splitCells || {};
            const newSplitCells = {};
            for (const [cellKey, val] of Object.entries(oldSplitCells)) {
                if (cellKey.includes(`_${colId}`)) continue;
                newSplitCells[cellKey] = val;
            }

            // Clean up cellOptions: remove entries for deleted column
            const oldCellOptions = table.cellOptions || {};
            const newCellOptions = {};
            for (const [cellKey, opts] of Object.entries(oldCellOptions)) {
                if (cellKey.includes(`_${colId}`) || cellKey.includes(`_${colId}_`)) continue;
                newCellOptions[cellKey] = opts;
            }

            updateTableCallback(
                {
                    ...withoutAction(table),
                    tableHead: newHead,
                    mergedCells: newMergedCells,
                    cellTypes: newCellTypes,
                    splitCells: newSplitCells,
                    cellOptions: newCellOptions,
                    cellRequired: dropColumnKeys(table.cellRequired, [colId]),
                    cellMinSelections: dropColumnKeys(table.cellMinSelections, [colId]),
                    cellReadOnly: dropColumnKeys(table.cellReadOnly, [colId]),
                    ...(table._selectedCellInfo?.colId === colId ? { _selectedCellInfo: null } : {}),
                },
                newBody
            );
            setSelectedCells([]);
            setSelectedCell(null);
        },
        [readOnly, tableHead, tableBody, table, mergedCells, updateTableCallback]
    );

    // --- Cell type change (per-cell) ---
    const handleChangeCellType = useCallback(
        (rowIndex, colId, newType, splitSide) => {
            if (readOnly) return;
            const cellTypes = { ...(table.cellTypes || {}) };
            const cellKey = splitSide ? `${rowIndex}_${colId}_${splitSide}` : `${rowIndex}_${colId}`;
            cellTypes[cellKey] = newType;

            // Store per-cell options for radio/checkbox group
            const cellOptions = { ...(table.cellOptions || {}) };
            let newHead = tableHead;

            if (newType === CELL_TYPES.RADIO || newType === CELL_TYPES.CHECKBOX_GROUP || newType === CELL_TYPES.SELECT || newType === CELL_TYPES.MULTI_SELECT) {
                if (splitSide) {
                    // For split cells, store options per side
                    if (!cellOptions[cellKey]?.length) {
                        cellOptions[cellKey] = [{ id: uuidv4(), name: 'Option 1' }];
                    }
                } else {
                    // For regular cells, store per-cell options
                    if (!cellOptions[cellKey]?.length) {
                        cellOptions[cellKey] = [{ id: uuidv4(), name: 'Option 1' }];
                    }
                }
            }

            // Reset the specific cell value
            const newBody = tableBody.map((row, rIdx) => {
                if (rIdx !== rowIndex) return row;
                const newRow = { ...row };
                if (splitSide) {
                    // For split cells, update only the specific side
                    const cellVal = newRow[colId];
                    const current = typeof cellVal === 'object' && cellVal !== null ? cellVal : { left: cellVal || '', right: '' };
                    let resetVal;
                    if (newType === CELL_TYPES.CHECKBOX) resetVal = false;
                    else if (newType === CELL_TYPES.CHECKBOX_GROUP || newType === CELL_TYPES.MULTI_SELECT) resetVal = [];
                    else if (newType === CELL_TYPES.RADIO || newType === CELL_TYPES.SELECT) resetVal = null;
                    else resetVal = '';
                    newRow[colId] = { ...current, [splitSide]: resetVal };
                } else {
                    if (newType === CELL_TYPES.CHECKBOX) {
                        newRow[colId] = false;
                    } else if (newType === CELL_TYPES.CHECKBOX_GROUP || newType === CELL_TYPES.MULTI_SELECT) {
                        newRow[colId] = [];
                    } else if (newType === CELL_TYPES.RADIO || newType === CELL_TYPES.SELECT) {
                        newRow[colId] = null;
                    } else {
                        newRow[colId] = '';
                    }
                }
                return newRow;
            });
            updateTableCallback({ ...table, tableHead: newHead, cellTypes, cellOptions }, newBody);
        },
        [readOnly, tableHead, tableBody, table, updateTableCallback]
    );

    // --- Header editing ---
    const updateHeader = useCallback(
        (colId, newName) => {
            const newHead = tableHead.map((col) => (col.id === colId ? { ...col, name: newName } : col));
            updateTableCallback({ ...table, tableHead: newHead }, undefined);
        },
        [tableHead, table, updateTableCallback]
    );

    // --- Cell value change (builder mode) ---
    const handleCellChangeBuilder = useCallback(
        (rowIndex, colKey, cellVal) => {
            if (readOnly) return;
            const newBody = [...tableBody];
            if (!newBody[rowIndex]) newBody[rowIndex] = {};
            newBody[rowIndex] = { ...newBody[rowIndex], [colKey]: cellVal };
            updateTableCallback(undefined, newBody);
        },
        [readOnly, tableBody, updateTableCallback]
    );

    // --- Multi-select (header and body are separate zones) ---
    const handleCellClick = useCallback(
        (e, rowIndex, colIndex, splitSide) => {
            if (readOnly || isFillerMode) {
                setSelectedCell({ rowIndex, colIndex });
                // Auto-focus editable element in the clicked cell
                const td = e.currentTarget;
                if (td) {
                    requestAnimationFrame(() => {
                        const editable = td.querySelector('textarea, select, [contenteditable="true"]');
                        if (editable && document.activeElement !== editable) {
                            editable.focus();
                        }
                    });
                }
                return;
            }
            const cell = { rowIndex, colIndex };
            const isHeader = rowIndex === -1;
            let newSelectedCells;
            if (e.shiftKey || e.ctrlKey || e.metaKey) {
                setSelectedCells((prev) => {
                    const sameZone = prev.filter((c) => (isHeader ? c.rowIndex === -1 : c.rowIndex >= 0));
                    const exists = sameZone.find((c) => c.rowIndex === rowIndex && c.colIndex === colIndex);
                    if (exists) {
                        newSelectedCells = sameZone.filter((c) => !(c.rowIndex === rowIndex && c.colIndex === colIndex));
                    } else {
                        newSelectedCells = [...sameZone, cell];
                    }
                    return newSelectedCells;
                });
            } else {
                newSelectedCells = [cell];
                setSelectedCells(newSelectedCells);
            }
            setSelectedCell(cell);

            // Auto-focus editable element in the clicked cell
            if (rowIndex >= 0) {
                const td = e.currentTarget;
                if (td) {
                    requestAnimationFrame(() => {
                        const editable = td.querySelector('textarea, select, [contenteditable="true"]');
                        if (editable && document.activeElement !== editable) {
                            editable.focus();
                        }
                    });
                }
            }

            // Sync selected cell info + full selection to table data for the
            // properties panel. In context mode it is kept on the table; the report
            // builder (onUpdate) must not persist transient selection metadata into
            // the saved report value, so it hands the cell to `onCellSelect` instead.
            if (rowIndex >= -1 && colIndex >= 0) {
                const col = tableHead[colIndex];
                if (col) {
                    const cellInfo = { rowIndex, colIndex, colId: col.id, splitSide: splitSide || null };
                    if (onUpdate) onCellSelect?.(cellInfo);
                    else updateTableCallback({ ...table, _selectedCellInfo: cellInfo }, undefined);
                }
            }
        },
        [readOnly, isFillerMode, tableHead, table, updateTableCallback, onUpdate, onCellSelect]
    );

    // --- Merge cells ---
    const handleMergeCells = useCallback(() => {
        if (selectedCells.length < 2) return;
        const minRow = Math.min(...selectedCells.map((c) => c.rowIndex));
        const maxRow = Math.max(...selectedCells.map((c) => c.rowIndex));
        const minCol = Math.min(...selectedCells.map((c) => c.colIndex));
        const maxCol = Math.max(...selectedCells.map((c) => c.colIndex));
        const rowSpan = maxRow - minRow + 1;
        const colSpan = maxCol - minCol + 1;
        const newMergedCells = { ...mergedCells };
        // Remove any existing merges within the new range
        for (const key of Object.keys(newMergedCells)) {
            const m = newMergedCells[key];
            if (m.startRow >= minRow && m.startRow <= maxRow && m.startCol >= minCol && m.startCol <= maxCol) {
                delete newMergedCells[key];
            }
        }
        newMergedCells[getMergeKey(minRow, minCol)] = {
            startRow: minRow,
            startCol: minCol,
            rowSpan,
            colSpan,
        };
        updateTableCallback({ ...table, mergedCells: newMergedCells }, undefined);
        setSelectedCells([{ rowIndex: minRow, colIndex: minCol }]);
    }, [selectedCells, mergedCells, table, updateTableCallback]);

    const handleUnmergeCells = useCallback(() => {
        let rowIndex, colIndex;
        if (contextMenu) {
            rowIndex = contextMenu.rowIndex;
            colIndex = contextMenu.colIndex;
        } else if (selectedCellsRef.current.length > 0) {
            rowIndex = selectedCellsRef.current[0].rowIndex;
            colIndex = selectedCellsRef.current[0].colIndex;
        } else if (table._selectedCellInfo) {
            rowIndex = table._selectedCellInfo.rowIndex;
            colIndex = table._selectedCellInfo.colIndex;
        } else {
            return;
        }
        const merge = isCellPartOfMerge(mergedCells, rowIndex, colIndex);
        if (!merge) return;
        const newMergedCells = { ...mergedCells };
        delete newMergedCells[getMergeKey(merge.startRow, merge.startCol)];
        updateTableCallback({ ...table, mergedCells: newMergedCells }, undefined);
        setSelectedCells([]);
    }, [contextMenu, mergedCells, table, updateTableCallback]);

    const handleSplitCell = useCallback(() => {
        let rowIndex, colIndex;
        if (contextMenu) {
            rowIndex = contextMenu.rowIndex;
            colIndex = contextMenu.colIndex;
        } else if (selectedCellsRef.current.length > 0) {
            rowIndex = selectedCellsRef.current[0].rowIndex;
            colIndex = selectedCellsRef.current[0].colIndex;
        } else if (table._selectedCellInfo) {
            rowIndex = table._selectedCellInfo.rowIndex;
            colIndex = table._selectedCellInfo.colIndex;
        } else {
            return;
        }

        // If it's a merged cell, just unmerge
        const merge = getMergeForCell(mergedCells, rowIndex, colIndex);
        if (merge) {
            const newMergedCells = { ...mergedCells };
            delete newMergedCells[getMergeKey(rowIndex, colIndex)];
            updateTableCallback({ ...table, mergedCells: newMergedCells }, undefined);
            return;
        }

        if (rowIndex < 0) return;
        const col = tableHead[colIndex];
        if (!col) return;
        // A Read Only cell of a note being filled keeps its value: split / unsplit would reshape it
        // (and a split half of a Read Only cell is locked too, so it could never be filled).
        if (isTypeLockedCell(rowIndex, colIndex)) return;

        const splitCells = { ...(table.splitCells || {}) };
        const splitKey = `${rowIndex}_${col.id}`;

        if (splitCells[splitKey]) {
            // Already split — unsplit it
            delete splitCells[splitKey];
            // Convert value back to string (take left part)
            const newBody = tableBody.map((row, rIdx) => {
                if (rIdx !== rowIndex) return row;
                const cellVal = row[col.id];
                const leftVal = typeof cellVal === 'object' && cellVal !== null ? cellVal.left || '' : cellVal || '';
                return { ...row, [col.id]: leftVal };
            });
            updateTableCallback({ ...table, splitCells }, newBody);
        } else {
            // Split the cell — store as { left, right }
            splitCells[splitKey] = true;
            const newBody = tableBody.map((row, rIdx) => {
                if (rIdx !== rowIndex) return row;
                const cellVal = row[col.id] || '';
                return { ...row, [col.id]: { left: cellVal, right: '' } };
            });
            updateTableCallback({ ...table, splitCells }, newBody);
        }
    }, [contextMenu, mergedCells, table, tableHead, tableBody, updateTableCallback, isFillerMode, lockReadOnlyCells]);

    // Listen for actions dispatched from the properties panel
    useEffect(() => {
        const action = table._action;
        const actionId = table._actionId;
        if (!action || !actionId || actionId === lastActionIdRef.current) return;
        lastActionIdRef.current = actionId;

        switch (action) {
            case 'addRow':
                handleAddRow();
                break;
            case 'merge':
                handleMergeCells();
                break;
            case 'unmerge':
                handleUnmergeCells();
                break;
            case 'split':
                handleSplitCell();
                break;
            case 'addColumn':
                handleAddColumn();
                break;
            case 'removeColumn':
                if (table._actionPayload) handleRemoveColumn(table._actionPayload);
                break;
            case 'updateColumn': {
                const { colId, patch } = table._actionPayload || {};
                if (colId && patch) handleUpdateColumn(colId, patch);
                break;
            }
            case 'setCellRequired': {
                const cells =
                    selectedCellsRef.current.length > 0
                        ? selectedCellsRef.current.filter((c) => c.rowIndex >= 0)
                        : table._selectedCellInfo?.rowIndex >= 0
                          ? [table._selectedCellInfo]
                          : [];
                if (!cells.length) break;
                const cellRequired = { ...(table.cellRequired || {}) };
                cells.forEach((cell) => {
                    const col = tableHead[cell.colIndex];
                    if (!col) return;
                    const key = `${cell.rowIndex}_${col.id}`;
                    // Matching the column needs no override.
                    if (!!table._actionPayload === !!col.required) delete cellRequired[key];
                    else cellRequired[key] = !!table._actionPayload;
                });
                updateTableCallback({ ...withoutAction(table), cellRequired }, undefined);
                break;
            }
            case 'setCellReadOnly': {
                const cells =
                    selectedCellsRef.current.length > 0
                        ? selectedCellsRef.current.filter((c) => c.rowIndex >= 0)
                        : table._selectedCellInfo?.rowIndex >= 0
                          ? [table._selectedCellInfo]
                          : [];
                if (!cells.length) break;
                // Only `true` is stored: a cell that is not Read Only has no entry.
                const cellReadOnly = { ...(table.cellReadOnly || {}) };
                cells.forEach((cell) => {
                    const col = tableHead[cell.colIndex];
                    if (!col) return;
                    const key = `${cell.rowIndex}_${col.id}`;
                    if (table._actionPayload) cellReadOnly[key] = true;
                    else delete cellReadOnly[key];
                });
                updateTableCallback({ ...withoutAction(table), cellReadOnly }, undefined);
                break;
            }
            case 'setCellMinSelections': {
                const cells =
                    selectedCellsRef.current.length > 0
                        ? selectedCellsRef.current.filter((c) => c.rowIndex >= 0)
                        : table._selectedCellInfo?.rowIndex >= 0
                          ? [table._selectedCellInfo]
                          : [];
                if (!cells.length) break;
                const next = toMinSelections(table._actionPayload);
                const cellMinSelections = { ...(table.cellMinSelections || {}) };
                cells.forEach((cell) => {
                    const col = tableHead[cell.colIndex];
                    if (!col) return;
                    const key = `${cell.rowIndex}_${col.id}`;
                    // Matching the column needs no override.
                    if (next === toMinSelections(col.minSelections)) delete cellMinSelections[key];
                    else cellMinSelections[key] = next;
                });
                updateTableCallback({ ...withoutAction(table), cellMinSelections }, undefined);
                break;
            }
            case 'changeCellType': {
                const newType = table._actionPayload;
                if (!newType) break;
                const storedCells = selectedCellsRef.current || [];
                const cellsToChange =
                    storedCells.length > 0
                        ? storedCells.filter((c) => c.rowIndex >= 0)
                        : table._selectedCellInfo
                          ? [{ rowIndex: table._selectedCellInfo.rowIndex, colIndex: table._selectedCellInfo.colIndex }]
                          : [];

                if (cellsToChange.length === 0) break;

                const newCellTypes = { ...(table.cellTypes || {}) };
                const newCellOptions = { ...(table.cellOptions || {}) };
                const affectedRows = new Set();

                // Find existing options from any cell in the selection to use as template
                let templateOptions = null;
                if (newType === CELL_TYPES.RADIO || newType === CELL_TYPES.CHECKBOX_GROUP || newType === CELL_TYPES.SELECT || newType === CELL_TYPES.MULTI_SELECT) {
                    cellsToChange.forEach((cell) => {
                        if (templateOptions) return;
                        const col = tableHead[cell.colIndex];
                        if (!col) return;
                        const cellKey = `${cell.rowIndex}_${col.id}`;
                        if (newCellOptions[cellKey]?.length) {
                            templateOptions = newCellOptions[cellKey];
                        }
                    });
                }

                cellsToChange.forEach((cell) => {
                    const col = tableHead[cell.colIndex];
                    if (!col) return;
                    const cellKey = `${cell.rowIndex}_${col.id}`;
                    newCellTypes[cellKey] = newType;
                    if (newType === CELL_TYPES.RADIO || newType === CELL_TYPES.CHECKBOX_GROUP || newType === CELL_TYPES.SELECT || newType === CELL_TYPES.MULTI_SELECT) {
                        if (!newCellOptions[cellKey]?.length) {
                            newCellOptions[cellKey] = templateOptions
                                ? templateOptions.map((opt) => ({ ...opt, id: uuidv4() }))
                                : [{ id: uuidv4(), name: 'Option 1' }];
                        }
                    }
                    affectedRows.add(cell.rowIndex);
                });

                const newBody = tableBody.map((row, rIdx) => {
                    if (!affectedRows.has(rIdx)) return row;
                    const newRow = { ...row };
                    cellsToChange
                        .filter((c) => c.rowIndex === rIdx)
                        .forEach((cell) => {
                            const col = tableHead[cell.colIndex];
                            if (!col) return;
                            if (newType === CELL_TYPES.CHECKBOX) newRow[col.id] = false;
                            else if (newType === CELL_TYPES.CHECKBOX_GROUP || newType === CELL_TYPES.MULTI_SELECT) newRow[col.id] = [];
                            else if (newType === CELL_TYPES.RADIO || newType === CELL_TYPES.SELECT) newRow[col.id] = null;
                            else newRow[col.id] = '';
                        });
                    return newRow;
                });

                const cleanTable = { ...table };
                delete cleanTable._action;
                delete cleanTable._actionId;
                delete cleanTable._actionPayload;
                updateTableCallback({ ...cleanTable, cellTypes: newCellTypes, cellOptions: newCellOptions }, newBody);
                break;
            }
            case 'addOption': {
                const storedCells2 = selectedCellsRef.current || [];
                const cellsForOpts =
                    storedCells2.length > 0
                        ? storedCells2.filter((c) => c.rowIndex >= 0)
                        : table._selectedCellInfo
                          ? [{ rowIndex: table._selectedCellInfo.rowIndex, colIndex: table._selectedCellInfo.colIndex }]
                          : [];

                if (cellsForOpts.length === 0) break;

                const newCellOptions = { ...(table.cellOptions || {}) };
                cellsForOpts.forEach((cell) => {
                    const col = tableHead[cell.colIndex];
                    if (!col) return;
                    const cellKey = `${cell.rowIndex}_${col.id}`;
                    const existing = newCellOptions[cellKey] || col.options || [];
                    newCellOptions[cellKey] = [...existing, { id: uuidv4(), name: `Option ${existing.length + 1}` }];
                });

                const cleanTable = { ...table };
                delete cleanTable._action;
                delete cleanTable._actionId;
                updateTableCallback({ ...cleanTable, cellOptions: newCellOptions }, undefined);
                break;
            }
            case 'removeOption': {
                const optIndex = table._actionPayload;
                if (optIndex === undefined || optIndex === null) break;

                const storedCells3 = selectedCellsRef.current || [];
                const cellsForOpts =
                    storedCells3.length > 0
                        ? storedCells3.filter((c) => c.rowIndex >= 0)
                        : table._selectedCellInfo
                          ? [{ rowIndex: table._selectedCellInfo.rowIndex, colIndex: table._selectedCellInfo.colIndex }]
                          : [];

                if (cellsForOpts.length === 0) break;

                const newCellOptions = { ...(table.cellOptions || {}) };
                cellsForOpts.forEach((cell) => {
                    const col = tableHead[cell.colIndex];
                    if (!col) return;
                    const cellKey = `${cell.rowIndex}_${col.id}`;
                    const existing = newCellOptions[cellKey] || col.options || [];
                    newCellOptions[cellKey] = existing.filter((_, i) => i !== optIndex);
                });

                const cleanTable = { ...table };
                delete cleanTable._action;
                delete cleanTable._actionId;
                delete cleanTable._actionPayload;
                updateTableCallback({ ...cleanTable, cellOptions: newCellOptions }, undefined);
                break;
            }
            case 'renameOption': {
                const { optIndex: renameIdx, value: newName } = table._actionPayload || {};
                if (renameIdx === undefined || renameIdx === null) break;

                const cellsForRename =
                    selectedCellsRef.current.length > 0
                        ? selectedCellsRef.current.filter((c) => c.rowIndex >= 0)
                        : table._selectedCellInfo
                          ? [{ rowIndex: table._selectedCellInfo.rowIndex, colIndex: table._selectedCellInfo.colIndex }]
                          : [];

                if (cellsForRename.length === 0) break;

                const newCellOptions = { ...(table.cellOptions || {}) };
                cellsForRename.forEach((cell) => {
                    const col = tableHead[cell.colIndex];
                    if (!col) return;
                    const cellKey = `${cell.rowIndex}_${col.id}`;
                    const existing = [...(newCellOptions[cellKey] || col.options || [])];
                    if (existing[renameIdx]) {
                        existing[renameIdx] = { ...existing[renameIdx], name: newName };
                        newCellOptions[cellKey] = existing;
                    }
                });

                const cleanTable2 = { ...table };
                delete cleanTable2._action;
                delete cleanTable2._actionId;
                delete cleanTable2._actionPayload;
                updateTableCallback({ ...cleanTable2, cellOptions: newCellOptions }, undefined);
                break;
            }
            case 'setStyle': {
                const { target, styleKey, value, cell: payloadCell } = table._actionPayload || {};
                const storedCells = selectedCellsRef.current || [];
                // The report builder has no `_selectedCellInfo`; Properties sends the cell it shows with the action.
                const fallbackCell = table._selectedCellInfo || payloadCell;
                const cellsToChange = storedCells.length > 0
                    ? storedCells
                    : fallbackCell
                      ? [{ rowIndex: fallbackCell.rowIndex, colIndex: fallbackCell.colIndex }]
                      : [];

                if (cellsToChange.length === 0) break;

                const newRowStyles = { ...(table.rowStyles || {}) };
                const newColStyles = { ...(table.colStyles || {}) };
                const newCellStyles = { ...(table.cellStyles || {}) };
                // A whole-cell text style also comes off the text inside the cell, which would otherwise keep its own.
                let newBody = null;
                const clearCellText = (rowIndex, colId) => {
                    const cellVal = (newBody || tableBody)[rowIndex]?.[colId];
                    const cleared =
                        cellVal && typeof cellVal === 'object'
                            ? { ...cellVal, left: clearCellHtmlStyle(cellVal.left, styleKey), right: clearCellHtmlStyle(cellVal.right, styleKey) }
                            : clearCellHtmlStyle(cellVal, styleKey);
                    const changed = cellVal && typeof cellVal === 'object' ? cleared.left !== cellVal.left || cleared.right !== cellVal.right : cleared !== cellVal;
                    if (!changed) return;
                    newBody = newBody || [...tableBody];
                    newBody[rowIndex] = { ...newBody[rowIndex], [colId]: cleared };
                };

                cellsToChange.forEach((cell) => {
                    const col = tableHead[cell.colIndex];
                    if (!col) return;
                    if (target === 'row' && cell.rowIndex >= 0) {
                        newRowStyles[cell.rowIndex] = { ...(newRowStyles[cell.rowIndex] || {}), [styleKey]: value };
                    } else if (target === 'col') {
                        newColStyles[col.id] = { ...(newColStyles[col.id] || {}), [styleKey]: value };
                    } else if (target === 'cell') {
                        const cellKey = `${cell.rowIndex}_${col.id}`;
                        const updatedStyle = { ...(newCellStyles[cellKey] || {}), [styleKey]: value };
                        if (styleKey === 'borderColor' || styleKey === 'borderWidth') {
                            updatedStyle.borderStyle = value ? 'solid' : undefined;
                        }
                        newCellStyles[cellKey] = updatedStyle;
                        if (cell.rowIndex >= 0) clearCellText(cell.rowIndex, col.id);
                    }
                });

                const cleanTable = { ...table };
                delete cleanTable._action;
                delete cleanTable._actionId;
                delete cleanTable._actionPayload;

                updateTableCallback(
                    { ...cleanTable, rowStyles: newRowStyles, colStyles: newColStyles, cellStyles: newCellStyles },
                    newBody || undefined
                );
                break;
            }
            default:
                break;
        }
    }, [
        table._action,
        table._actionId,
        handleAddRow,
        handleMergeCells,
        handleUnmergeCells,
        handleSplitCell,
        handleAddColumn,
        handleRemoveColumn,
        handleUpdateColumn,
        tableHead,
        tableBody,
        table,
        updateTableCallback,
    ]);

    // --- Context menu ---
    const handleContextMenu = useCallback(
        (e, rowIndex, colIndex, splitSide) => {
            if (readOnly || isFillerMode) return;
            e.preventDefault();
            e.stopPropagation();

            // Select the cell on right-click (same as left-click without modifier)
            const cell = { rowIndex, colIndex };
            const alreadySelected = selectedCells.some((c) => c.rowIndex === rowIndex && c.colIndex === colIndex);
            if (!alreadySelected) {
                setSelectedCells([cell]);
            }
            setSelectedCell(cell);

            const col = tableHead[colIndex];
            const cellTypes = table.cellTypes || {};
            const cellKey = splitSide ? `${rowIndex}_${col?.id}_${splitSide}` : `${rowIndex}_${col?.id}`;
            const perCellType = cellTypes[cellKey];
            setContextMenu({
                x: e.clientX,
                y: e.clientY,
                rowIndex,
                colIndex,
                colId: col?.id,
                cellType: perCellType || col?.cellType || CELL_TYPES.TEXT,
                splitSide: splitSide || null,
            });
        },
        [readOnly, isFillerMode, tableHead, table, selectedCells]
    );

    const contextCanMerge = selectedCells.length >= 2;
    const contextIsMergedCell = contextMenu ? !!isCellPartOfMerge(mergedCells, contextMenu.rowIndex, contextMenu.colIndex) : false;
    // A merged cell can always be unmerged; a body cell split, unless it is a note's Read Only cell.
    const contextCanSplit = contextMenu
        ? !!getMergeForCell(mergedCells, contextMenu.rowIndex, contextMenu.colIndex) ||
          (contextMenu.rowIndex >= 0 && !isTypeLockedCell(contextMenu.rowIndex, contextMenu.colIndex))
        : false;
    const contextBodyCells = contextMenu
        ? selectedCells.some((c) => c.rowIndex >= 0)
            ? selectedCells.filter((c) => c.rowIndex >= 0)
            : [{ rowIndex: contextMenu.rowIndex, colIndex: contextMenu.colIndex }]
        : [];
    // Right-clicked a body cell and every cell it would change is Read Only: nothing to change.
    const contextCellTypeLocked =
        !!contextMenu && contextMenu.rowIndex >= 0 && contextBodyCells.every((c) => isTypeLockedCell(c.rowIndex, c.colIndex));

    const handleContextAction = useCallback(
        (action, payload) => {
            if (!contextMenu) return;
            switch (action) {
                case CONTEXT_ACTIONS.ADD_ROW:
                    handleAddRow();
                    break;
                case CONTEXT_ACTIONS.DELETE_ROW: {
                    // Get all unique selected body row indices, sorted descending
                    const selectedRowIndices = [...new Set(selectedCells.filter((c) => c.rowIndex >= 0).map((c) => c.rowIndex))].sort(
                        (a, b) => b - a
                    );
                    const rowsToDelete = selectedRowIndices.length > 0 ? selectedRowIndices : [contextMenu.rowIndex];
                    if (rowsLocked && rowsToDelete.length >= rowsToRender.length) break;

                    if (isFillerMode) {
                        rowsToDelete.forEach((ri) => remove(ri));
                    } else {
                        let currentBody = [...readCurrentBody()];

                        // Delete rows from bottom up (indices are already sorted descending)
                        const deleteSet = new Set(rowsToDelete);
                        const newBody = currentBody.filter((_, i) => !deleteSet.has(i));

                        // Build a mapping from old index to new index
                        let newIdx = 0;
                        const indexMap = {};
                        for (let i = 0; i < currentBody.length; i++) {
                            if (!deleteSet.has(i)) {
                                indexMap[i] = newIdx++;
                            }
                        }

                        // Clean up mergedCells
                        const newMergedCells = {};
                        for (const [key, merge] of Object.entries(mergedCells)) {
                            if (merge.startRow === -1) {
                                newMergedCells[key] = merge;
                                continue;
                            }
                            let newStartRow = merge.startRow;
                            let newRowSpan = merge.rowSpan;
                            // Count how many deleted rows fall inside this merge
                            for (const delRow of rowsToDelete) {
                                if (delRow >= merge.startRow && delRow < merge.startRow + merge.rowSpan) {
                                    newRowSpan--;
                                }
                            }
                            if (newRowSpan <= 0) continue;
                            // Find the new start row index
                            if (indexMap[newStartRow] !== undefined) {
                                newStartRow = indexMap[newStartRow];
                            } else {
                                // Start row was deleted, find next available
                                for (let r = merge.startRow; r < merge.startRow + merge.rowSpan; r++) {
                                    if (indexMap[r] !== undefined) {
                                        newStartRow = indexMap[r];
                                        break;
                                    }
                                }
                            }
                            newMergedCells[getMergeKey(newStartRow, merge.startCol)] = {
                                ...merge,
                                startRow: newStartRow,
                                rowSpan: newRowSpan,
                            };
                        }

                        // Clean up cellTypes
                        const oldCellTypes = table.cellTypes || {};
                        const newCellTypes = {};
                        for (const [cellKey, type] of Object.entries(oldCellTypes)) {
                            const firstUnderscore = cellKey.indexOf('_');
                            const r = parseInt(cellKey.substring(0, firstUnderscore), 10);
                            const rest = cellKey.substring(firstUnderscore + 1);
                            if (deleteSet.has(r)) continue;
                            newCellTypes[`${indexMap[r]}_${rest}`] = type;
                        }

                        // Clean up splitCells
                        const oldSplitCells = table.splitCells || {};
                        const newSplitCells = {};
                        for (const [cellKey, val] of Object.entries(oldSplitCells)) {
                            const firstUnderscore = cellKey.indexOf('_');
                            const r = parseInt(cellKey.substring(0, firstUnderscore), 10);
                            const rest = cellKey.substring(firstUnderscore + 1);
                            if (deleteSet.has(r)) continue;
                            newSplitCells[`${indexMap[r]}_${rest}`] = val;
                        }

                        // Clean up cellOptions
                        const oldCellOptions = table.cellOptions || {};
                        const newCellOptions = {};
                        for (const [cellKey, opts] of Object.entries(oldCellOptions)) {
                            const firstUnderscore = cellKey.indexOf('_');
                            const r = parseInt(cellKey.substring(0, firstUnderscore), 10);
                            const rest = cellKey.substring(firstUnderscore + 1);
                            if (deleteSet.has(r)) continue;
                            newCellOptions[`${indexMap[r]}_${rest}`] = opts;
                        }

                        updateTableCallback(
                            {
                                ...table,
                                mergedCells: newMergedCells,
                                cellTypes: newCellTypes,
                                splitCells: newSplitCells,
                                cellOptions: newCellOptions,
                                cellRequired: shiftRowKeys(table.cellRequired, (r) => (deleteSet.has(r) ? null : indexMap[r])),
                                cellMinSelections: shiftRowKeys(table.cellMinSelections, (r) => (deleteSet.has(r) ? null : indexMap[r])),
                                cellReadOnly: shiftRowKeys(table.cellReadOnly, (r) => (deleteSet.has(r) ? null : indexMap[r])),
                            },
                            newBody
                        );
                    }
                    setSelectedCells([]);
                    break;
                }
                case CONTEXT_ACTIONS.ADD_COLUMN:
                    handleAddColumn();
                    break;
                case CONTEXT_ACTIONS.DELETE_COLUMN: {
                    // Get all unique selected column indices
                    const selectedColIndices = [...new Set(selectedCells.map((c) => c.colIndex))];
                    const colsToDelete = selectedColIndices.length > 0 ? selectedColIndices : [contextMenu.colIndex];
                    const deleteColIds = new Set(colsToDelete.map((ci) => tableHead[ci]?.id).filter(Boolean));

                    const newHead = tableHead.filter((col) => !deleteColIds.has(col.id));
                    const newBody = tableBody.map((row) => {
                        const newRow = { ...row };
                        deleteColIds.forEach((colId) => delete newRow[colId]);
                        return newRow;
                    });

                    // Build column index map (old index -> new index)
                    const deleteColIndexSet = new Set(colsToDelete);
                    let newColIdx = 0;
                    const colIndexMap = {};
                    for (let i = 0; i < tableHead.length; i++) {
                        if (!deleteColIndexSet.has(i)) {
                            colIndexMap[i] = newColIdx++;
                        }
                    }

                    // Clean up mergedCells
                    const newMergedCells = {};
                    for (const [key, merge] of Object.entries(mergedCells)) {
                        let newColSpan = merge.colSpan;
                        for (const delCol of colsToDelete) {
                            if (delCol >= merge.startCol && delCol < merge.startCol + merge.colSpan) {
                                newColSpan--;
                            }
                        }
                        if (newColSpan <= 0) continue;
                        let newStartCol = merge.startCol;
                        if (colIndexMap[newStartCol] !== undefined) {
                            newStartCol = colIndexMap[newStartCol];
                        } else {
                            let found = false;
                            for (let c = merge.startCol; c < merge.startCol + merge.colSpan; c++) {
                                if (colIndexMap[c] !== undefined) {
                                    newStartCol = colIndexMap[c];
                                    found = true;
                                    break;
                                }
                            }
                            if (!found) continue;
                        }
                        newMergedCells[getMergeKey(merge.startRow, newStartCol)] = {
                            ...merge,
                            startCol: newStartCol,
                            colSpan: newColSpan,
                        };
                    }

                    // Clean up cellTypes: remove entries for deleted columns
                    const oldCellTypes2 = table.cellTypes || {};
                    const newCellTypes2 = {};
                    for (const [cellKey, type] of Object.entries(oldCellTypes2)) {
                        const shouldRemove = [...deleteColIds].some(
                            (colId) => cellKey.includes(`_${colId}`) || cellKey.includes(`_${colId}_`)
                        );
                        if (!shouldRemove) newCellTypes2[cellKey] = type;
                    }

                    // Clean up splitCells: remove entries for deleted columns
                    const oldSplitCells2 = table.splitCells || {};
                    const newSplitCells2 = {};
                    for (const [cellKey, val] of Object.entries(oldSplitCells2)) {
                        const shouldRemove = [...deleteColIds].some((colId) => cellKey.includes(`_${colId}`));
                        if (!shouldRemove) newSplitCells2[cellKey] = val;
                    }

                    // Clean up cellOptions: remove entries for deleted columns
                    const oldCellOptions2 = table.cellOptions || {};
                    const newCellOptions2 = {};
                    for (const [cellKey, opts] of Object.entries(oldCellOptions2)) {
                        const shouldRemove = [...deleteColIds].some(
                            (colId) => cellKey.includes(`_${colId}`) || cellKey.includes(`_${colId}_`)
                        );
                        if (!shouldRemove) newCellOptions2[cellKey] = opts;
                    }

                    updateTableCallback(
                        {
                            ...table,
                            tableHead: newHead,
                            mergedCells: newMergedCells,
                            cellTypes: newCellTypes2,
                            splitCells: newSplitCells2,
                            cellOptions: newCellOptions2,
                            cellRequired: dropColumnKeys(table.cellRequired, [...deleteColIds]),
                            cellMinSelections: dropColumnKeys(table.cellMinSelections, [...deleteColIds]),
                            cellReadOnly: dropColumnKeys(table.cellReadOnly, [...deleteColIds]),
                        },
                        newBody
                    );
                    setSelectedCells([]);
                    break;
                }
                case CONTEXT_ACTIONS.CHANGE_CELL_TYPE: {
                    // Right-clicked a header: the type belongs to the whole column.
                    // The body-cell path below filters header cells out, so this
                    // used to do nothing. Always applied, even when the column
                    // already has this type, so per-cell overrides are cleared.
                    if (contextMenu.rowIndex === -1) {
                        const headerCols = selectedCellsRef.current.filter((c) => c.rowIndex === -1).map((c) => c.colIndex);
                        const colIds = (headerCols.length ? headerCols : [contextMenu.colIndex])
                            .map((colIndex) => tableHead[colIndex]?.id)
                            .filter(Boolean);
                        if (!colIds.length) break;
                        const newHead = tableHead.map((col) => {
                            if (!colIds.includes(col.id)) return col;
                            const nextCol = { ...col, cellType: payload };
                            if (isOptionCellType(payload) && !nextCol.options?.length) {
                                nextCol.options = [{ id: uuidv4(), name: 'Option 1' }];
                            }
                            return nextCol;
                        });
                        // Read Only cells of a note being filled keep their value and type (`resetColumnsForType`).
                        const { newBody, ...resetMaps } = resetColumnsForType({
                            table,
                            tableHead,
                            tableBody,
                            colIds,
                            newType: payload,
                            isLocked: isTypeLockedCell,
                        });
                        updateTableCallback({ ...table, tableHead: newHead, ...resetMaps }, newBody);
                        break;
                    }

                    const cellsToChange = (
                        selectedCellsRef.current.length > 0
                            ? selectedCellsRef.current.filter((c) => c.rowIndex >= 0)
                            : [{ rowIndex: contextMenu.rowIndex, colIndex: contextMenu.colIndex }]
                    ).filter((c) => !isTypeLockedCell(c.rowIndex, c.colIndex));
                    if (!cellsToChange.length) break;

                    const newCellTypes = { ...(table.cellTypes || {}) };
                    const newCellOptions = { ...(table.cellOptions || {}) };
                    const affectedRows = new Set();

                    // Find existing options from any cell in the selection to use as template
                    let templateOptions = null;
                    if (payload === CELL_TYPES.RADIO || payload === CELL_TYPES.CHECKBOX_GROUP || payload === CELL_TYPES.SELECT || payload === CELL_TYPES.MULTI_SELECT) {
                        cellsToChange.forEach((cell) => {
                            if (templateOptions) return;
                            const col = tableHead[cell.colIndex];
                            if (!col) return;
                            const cellKey = `${cell.rowIndex}_${col.id}`;
                            if (newCellOptions[cellKey]?.length) {
                                templateOptions = newCellOptions[cellKey];
                            }
                        });
                    }

                    cellsToChange.forEach((cell) => {
                        const col = tableHead[cell.colIndex];
                        if (!col) return;
                        const splitSide =
                            cell.rowIndex === contextMenu.rowIndex && cell.colIndex === contextMenu.colIndex ? contextMenu.splitSide : null;
                        const cellKey = splitSide ? `${cell.rowIndex}_${col.id}_${splitSide}` : `${cell.rowIndex}_${col.id}`;
                        newCellTypes[cellKey] = payload;

                        if (payload === CELL_TYPES.RADIO || payload === CELL_TYPES.CHECKBOX_GROUP || payload === CELL_TYPES.SELECT || payload === CELL_TYPES.MULTI_SELECT) {
                            if (!newCellOptions[cellKey]?.length) {
                                newCellOptions[cellKey] = templateOptions
                                    ? templateOptions.map((opt) => ({ ...opt, id: uuidv4() }))
                                    : [{ id: uuidv4(), name: 'Option 1' }];
                            }
                        }
                        affectedRows.add(cell.rowIndex);
                    });

                    // Reset cell values for affected rows
                    const newBody = tableBody.map((row, rIdx) => {
                        if (!affectedRows.has(rIdx)) return row;
                        const newRow = { ...row };
                        cellsToChange
                            .filter((c) => c.rowIndex === rIdx)
                            .forEach((cell) => {
                                const col = tableHead[cell.colIndex];
                                if (!col) return;
                                const splitSide =
                                    cell.rowIndex === contextMenu.rowIndex && cell.colIndex === contextMenu.colIndex
                                        ? contextMenu.splitSide
                                        : null;
                                if (splitSide) {
                                    const cellVal = newRow[col.id];
                                    const current =
                                        typeof cellVal === 'object' && cellVal !== null ? cellVal : { left: cellVal || '', right: '' };
                                    let resetVal;
                                    if (payload === CELL_TYPES.CHECKBOX) resetVal = false;
                                    else if (payload === CELL_TYPES.CHECKBOX_GROUP || payload === CELL_TYPES.MULTI_SELECT) resetVal = [];
                                    else if (payload === CELL_TYPES.RADIO || payload === CELL_TYPES.SELECT) resetVal = null;
                                    else resetVal = '';
                                    newRow[col.id] = { ...current, [splitSide]: resetVal };
                                } else {
                                    if (payload === CELL_TYPES.CHECKBOX) newRow[col.id] = false;
                                    else if (payload === CELL_TYPES.CHECKBOX_GROUP || payload === CELL_TYPES.MULTI_SELECT) newRow[col.id] = [];
                                    else if (payload === CELL_TYPES.RADIO || payload === CELL_TYPES.SELECT) newRow[col.id] = null;
                                    else newRow[col.id] = '';
                                }
                            });
                        return newRow;
                    });

                    updateTableCallback({ ...table, cellTypes: newCellTypes, cellOptions: newCellOptions }, newBody);
                    break;
                }
                case CONTEXT_ACTIONS.MERGE_CELLS:
                    handleMergeCells();
                    break;
                case CONTEXT_ACTIONS.UNMERGE_CELLS:
                    handleUnmergeCells();
                    break;
                case CONTEXT_ACTIONS.SPLIT_CELL:
                    handleSplitCell();
                    break;
                default:
                    break;
            }
            setContextMenu(null);
        },
        [
            contextMenu,
            selectedCells,
            handleAddRow,
            handleAddColumn,
            handleChangeCellType,
            handleMergeCells,
            handleUnmergeCells,
            handleSplitCell,
            tableHead,
            tableBody,
            table,
            mergedCells,
            index,
            isFillerMode,
            remove,
            updateTableCallback,
            readCurrentBody,
            rowsLocked,
            rowsToRender,
            lockReadOnlyCells,
        ]
    );

    // --- Drag and drop ---
    const handleDragEnd = useCallback(
        (result) => {
            if (!result.destination) return;
            const srcIndex = result.source.index;
            const destIndex = result.destination.index;
            if (srcIndex === destIndex) return;

            const droppableId = result.source.droppableId;

            // Column reorder
            if (droppableId === `table-columns-${index}`) {
                const newHead = Array.from(tableHead);
                const [moved] = newHead.splice(srcIndex, 1);
                newHead.splice(destIndex, 0, moved);

                // Build old→new column index mapping
                const colMap = {};
                const tempColIndices = Array.from({ length: tableHead.length }, (_, i) => i);
                const [movedColIdx] = tempColIndices.splice(srcIndex, 1);
                tempColIndices.splice(destIndex, 0, movedColIdx);
                tempColIndices.forEach((oldIdx, newIdx) => {
                    colMap[oldIdx] = newIdx;
                });

                // Remap mergedCells column indices
                const oldMerged = table.mergedCells || {};
                const newMergedCells = {};
                for (const [, merge] of Object.entries(oldMerged)) {
                    let canRemap = true;
                    const newStartCol = colMap[merge.startCol];
                    if (newStartCol === undefined) {
                        canRemap = false;
                    }
                    if (canRemap && merge.colSpan > 1) {
                        const mappedCols = [];
                        for (let c = 0; c < merge.colSpan; c++) {
                            const mapped = colMap[merge.startCol + c];
                            if (mapped === undefined) {
                                canRemap = false;
                                break;
                            }
                            mappedCols.push(mapped);
                        }
                        if (canRemap) {
                            mappedCols.sort((a, b) => a - b);
                            for (let i = 1; i < mappedCols.length; i++) {
                                if (mappedCols[i] !== mappedCols[i - 1] + 1) {
                                    canRemap = false;
                                    break;
                                }
                            }
                            if (canRemap) {
                                const remappedStart = mappedCols[0];
                                newMergedCells[getMergeKey(merge.startRow, remappedStart)] = {
                                    ...merge,
                                    startCol: remappedStart,
                                };
                            }
                        }
                    } else if (canRemap) {
                        newMergedCells[getMergeKey(merge.startRow, newStartCol)] = {
                            ...merge,
                            startCol: newStartCol,
                        };
                    }
                }

                updateTableCallback({ ...table, tableHead: newHead, mergedCells: newMergedCells }, undefined);
                return;
            }

            // Row reorder
            if (isFillerMode) {
                move(srcIndex, destIndex);
            } else {
                const newBody = Array.from(tableBody);
                const [moved] = newBody.splice(srcIndex, 1);
                newBody.splice(destIndex, 0, moved);

                // Build old→new row index mapping
                const rowMap = {};
                const tempIndices = Array.from({ length: tableBody.length }, (_, i) => i);
                const [movedIdx] = tempIndices.splice(srcIndex, 1);
                tempIndices.splice(destIndex, 0, movedIdx);
                // tempIndices[newPos] = oldPos, we need oldPos → newPos
                tempIndices.forEach((oldIdx, newIdx) => {
                    rowMap[oldIdx] = newIdx;
                });

                // Remap mergedCells
                const oldMerged = table.mergedCells || {};
                const newMergedCells = {};
                for (const [, merge] of Object.entries(oldMerged)) {
                    if (merge.startRow === -1) {
                        // Header merges stay unchanged
                        newMergedCells[getMergeKey(merge.startRow, merge.startCol)] = merge;
                        continue;
                    }
                    // Check if all rows of this merge can be mapped
                    let canRemap = true;
                    const newStartRow = rowMap[merge.startRow];
                    if (newStartRow === undefined) {
                        canRemap = false;
                    }
                    // For multi-row merges, verify all rows in the span are still contiguous
                    if (canRemap && merge.rowSpan > 1) {
                        const mappedRows = [];
                        for (let r = 0; r < merge.rowSpan; r++) {
                            const mapped = rowMap[merge.startRow + r];
                            if (mapped === undefined) {
                                canRemap = false;
                                break;
                            }
                            mappedRows.push(mapped);
                        }
                        if (canRemap) {
                            mappedRows.sort((a, b) => a - b);
                            // Check contiguity
                            for (let i = 1; i < mappedRows.length; i++) {
                                if (mappedRows[i] !== mappedRows[i - 1] + 1) {
                                    canRemap = false;
                                    break;
                                }
                            }
                            if (canRemap) {
                                const remappedStart = mappedRows[0];
                                newMergedCells[getMergeKey(remappedStart, merge.startCol)] = {
                                    ...merge,
                                    startRow: remappedStart,
                                };
                            }
                        }
                    } else if (canRemap) {
                        newMergedCells[getMergeKey(newStartRow, merge.startCol)] = {
                            ...merge,
                            startRow: newStartRow,
                        };
                    }
                }

                // Helper to remap row-indexed keys like "rowIndex_rest"
                const remapRowKeys = (obj) => {
                    if (!obj) return {};
                    const result = {};
                    for (const [key, val] of Object.entries(obj)) {
                        const firstUnderscore = key.indexOf('_');
                        if (firstUnderscore === -1) {
                            result[key] = val;
                            continue;
                        }
                        const rStr = key.substring(0, firstUnderscore);
                        const rest = key.substring(firstUnderscore + 1);
                        const r = parseInt(rStr, 10);
                        if (isNaN(r) || rowMap[r] === undefined) {
                            result[key] = val;
                            continue;
                        }
                        result[`${rowMap[r]}_${rest}`] = val;
                    }
                    return result;
                };

                const newCellTypes = remapRowKeys(table.cellTypes);
                const newCellOptions = remapRowKeys(table.cellOptions);
                const newSplitCells = remapRowKeys(table.splitCells);
                const newCellStyles = remapRowKeys(table.cellStyles);
                const newCellRequired = remapRowKeys(table.cellRequired);
                const newCellMinSelections = remapRowKeys(table.cellMinSelections);
                const newCellReadOnly = remapRowKeys(table.cellReadOnly);

                const oldRowStyles = table.rowStyles || {};
                const newRowStyles = {};
                for (const [rStr, st] of Object.entries(oldRowStyles)) {
                    const r = parseInt(rStr, 10);
                    if (rowMap[r] !== undefined) {
                        newRowStyles[rowMap[r]] = st;
                    }
                }

                updateTableCallback(
                    {
                        ...table,
                        mergedCells: newMergedCells,
                        cellTypes: newCellTypes,
                        cellOptions: newCellOptions,
                        splitCells: newSplitCells,
                        cellStyles: newCellStyles,
                        cellRequired: newCellRequired,
                        cellMinSelections: newCellMinSelections,
                        cellReadOnly: newCellReadOnly,
                        rowStyles: newRowStyles,
                    },
                    newBody
                );
            }
        },
        [isFillerMode, move, tableBody, tableHead, table, updateTableCallback, index]
    );

    return (
        <div
            ref={tableWrapperRef}
            className={`table-element-wrapper ${!showBorders ? 'no-borders' : ''}`}
            style={{ marginBottom: marginBottom || 18, ...wrapperStyle }}
        >
            {isFillerMode && name && <ErrorAnchor name={name} />}
            {!isFillerMode && validationControl && name && (
                <Controller name={name} control={validationControl} rules={requiredRule} render={() => <ErrorAnchor name={name} />} />
            )}
            {!isFillerMode && !readOnly && (
                <div className="table-info-bar-inside">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0C66E4" strokeWidth="2">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                    <span>
                        Click cells to select • Hold <kbd>Shift</kbd> or <kbd>Ctrl</kbd> for multi-select • Double-click to edit •
                        Right-click for more options
                    </span>
                </div>
            )}
            <div className="table-element-container">
                <DragDropContext onDragEnd={handleDragEnd}>
                    <table className="table-element-box">
                        {showHeader && (
                            <thead>
                                <Droppable droppableId={`table-columns-${index}`} direction="horizontal">
                                    {(colDropProvided) => (
                                        <tr
                                            className="table-element-head-wrapper"
                                            ref={colDropProvided.innerRef}
                                            {...colDropProvided.droppableProps}
                                        >
                                            {!readOnly && !isFillerMode && <th className="table-drag-handle-col" />}
                                            {tableHead.map((col, hIndex) => {
                                                const headerMerge = getMergeForCell(mergedCells, -1, hIndex);
                                                if (isCellHidden(mergedCells, -1, hIndex)) return null;
                                                const isHeaderSelected = selectedCells.some(
                                                    (c) => c.rowIndex === -1 && c.colIndex === hIndex
                                                );

                                                return (
                                                    <Draggable
                                                        key={col.id || `col-${hIndex}`}
                                                        draggableId={col.id || `col-${hIndex}`}
                                                        index={hIndex}
                                                        isDragDisabled={readOnly || isFillerMode}
                                                    >
                                                        {(colDragProvided) => (
                                                            <th
                                                                ref={colDragProvided.innerRef}
                                                                {...colDragProvided.draggableProps}
                                                                className={`${isHeaderSelected ? 'table-cell-selected' : ''} ${headerMerge ? 'table-cell-merged' : ''}`}
                                                                onClick={(e) => handleCellClick(e, -1, hIndex)}
                                                                onContextMenu={(e) => handleContextMenu(e, -1, hIndex)}
                                                                {...(headerMerge ? { colSpan: headerMerge.colSpan } : {})}
                                                                style={{ ...colDragProvided.draggableProps.style, color: '#172B4D', fontWeight: 600, fontSize: 12, ...((table.cellStyles || {})[`-1_${col.id}`] || {}) }}
                                                            >
                                                                <div className={`table-header-cell-inner${isFillerMode || readOnly ? '' : ' table-header-cell-stacked'}`}>
                                                                    {isFillerMode || readOnly ? (
                                                                        <span
                                                                            style={{
                                                                                color: 'inherit',
                                                                                fontWeight: 'inherit',
                                                                                wordBreak: 'break-word',
                                                                                fontSize: 'inherit',
                                                                                fontStyle: 'inherit',
                                                                                textDecoration: 'inherit',
                                                                            }}
                                                                        >
                                                                            {col?.name || ''}
                                                                            {col.required && <RequiredMark />}
                                                                        </span>
                                                                    ) : (
                                                                        <>
                                                                            {/* Builder: the icons on a row of their own, the label under them at full width. */}
                                                                            <div className="table-header-cell-icons">
                                                                                <div
                                                                                    {...colDragProvided.dragHandleProps}
                                                                                    className="table-col-drag-handle"
                                                                                >
                                                                                    <img src={Images.dragIcon} alt="" className="table-drag-icon" />
                                                                                </div>
                                                                                <CellTypeIcon type={col.cellType || CELL_TYPES.TEXT} color="#626F86" />
                                                                                <span className="table-header-cell-icons-spacer" />
                                                                            <button
                                                                                type="button"
                                                                                className="session-data-column-chevron"
                                                                                title="Column type"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    if (typePicker?.colId === col.id) {
                                                                                        setTypePicker(null);
                                                                                        return;
                                                                                    }
                                                                                    const rect = e.currentTarget.closest('th').getBoundingClientRect();
                                                                                    setTypePicker({
                                                                                        colId: col.id,
                                                                                        position: { top: rect.bottom + 4, left: rect.left, width: rect.width },
                                                                                    });
                                                                                }}
                                                                            >
                                                                                <ChevronIcon open={typePicker?.colId === col.id} />
                                                                            </button>
                                                                            {tableHead.length > 1 && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="session-data-column-remove"
                                                                                    title="Remove column"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleRemoveColumn(col.id);
                                                                                    }}
                                                                                >
                                                                                    <img src={Images.trash} alt="remove column" />
                                                                                </button>
                                                                            )}
                                                                            </div>
                                                                            <div className="table-header-cell-label">
                                                                                <span
                                                                                    contentEditable
                                                                                    suppressContentEditableWarning
                                                                                    onBlur={(e) => updateHeader(col.id, e.target.textContent)}
                                                                                    style={{
                                                                                        wordBreak: 'break-word',
                                                                                        display: 'block',
                                                                                        outline: 'none',
                                                                                        cursor: 'text',
                                                                                        flex: 1,
                                                                                        color: 'inherit',
                                                                                        fontWeight: 'inherit',
                                                                                        fontSize: 'inherit',
                                                                                        fontStyle: 'inherit',
                                                                                        textDecoration: 'inherit',
                                                                                    }}
                                                                                >
                                                                                    {col?.name || ''}
                                                                                </span>
                                                                                {col.required && <RequiredMark />}
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </div>
                                                                {typePicker?.colId === col.id && (
                                                                    <FieldPicker
                                                                        catalog={columnTypeCatalog}
                                                                        usedFields={[]}
                                                                        currentField={col.cellType || CELL_TYPES.TEXT}
                                                                        position={typePicker.position}
                                                                        onSelect={(cellType) => handleUpdateColumn(col.id, { cellType })}
                                                                        onClose={closeTypePicker}
                                                                    />
                                                                )}
                                                            </th>
                                                        )}
                                                    </Draggable>
                                                );
                                            })}
                                            {colDropProvided.placeholder}
                                        </tr>
                                    )}
                                </Droppable>
                            </thead>
                        )}
                        <Droppable droppableId={`table-rows-${index}`}>
                            {(droppableProvided) => (
                                <tbody ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                                    {rowsToRender.map((rowData, rIndex) => (
                                        <Draggable
                                            key={rowData._rowId || rowData.id || `row-${rIndex}`}
                                            draggableId={rowData._rowId || rowData.id || `row-${rIndex}`}
                                            index={rIndex}
                                            isDragDisabled={readOnly || isFillerMode}
                                        >
                                            {(draggableProvided, snapshot) => {
                                                const rowStyle = (table.rowStyles || {})[rIndex] || {};
                                                return (
                                                <tr
                                                    ref={draggableProvided.innerRef}
                                                    {...draggableProvided.draggableProps}
                                                    className={`table-element-body-wrapper ${snapshot.isDragging ? 'dragging' : ''}`}
                                                    style={{ ...draggableProvided.draggableProps.style }}
                                                >
                                                    {!readOnly && !isFillerMode && (
                                                        <td className="table-drag-handle-cell" {...draggableProvided.dragHandleProps}>
                                                            <img src={Images.dragIcon} alt="" className="table-drag-icon" />
                                                        </td>
                                                    )}
                                                    {tableHead.map((col, cIndex) => {
                                                        // Skip hidden cells (covered by a merge)
                                                        if (isCellHidden(mergedCells, rIndex, cIndex)) return null;

                                                        const colKey = getKey(col);
                                                        const cellTypesMap = table.cellTypes || {};
                                                        const perCellKey = `${rIndex}_${col.id}`;
                                                        const cellType = cellTypesMap[perCellKey] || col.cellType || CELL_TYPES.TEXT;
                                                        const isSelected =
                                                            selectedCells.some((c) => c.rowIndex === rIndex && c.colIndex === cIndex) ||
                                                            (selectedCell?.rowIndex === rIndex && selectedCell?.colIndex === cIndex);

                                                        // Check if this cell is the start of a merge
                                                        const merge = getMergeForCell(mergedCells, rIndex, cIndex);

                                                        // Read Only cell: staff see its value and cannot change it while filling
                                                        // (filler mode, or a note in builder mode — `lockReadOnlyCells`); the author
                                                        // still types it in the template builder. Builder mode (template or note)
                                                        // marks it with a lock — not when the whole table is read-only (note detail),
                                                        // where every cell is locked anyway.
                                                        const cellLocked = isCellReadOnly(table, col, rIndex);
                                                        const isFilling = isFillerMode || !!lockReadOnlyCells;
                                                        const cellReadOnlyProp = readOnly || (isFilling && cellLocked);
                                                        // Every required cell is marked — whether it inherits
                                                        // the column's Required or is set on its own. A Read Only
                                                        // cell is never required, so the two marks never meet.
                                                        const cellMark = isCellRequired(table, col, rIndex) ? (
                                                            <RequiredMark className="session-data-required-mark table-cell-required-mark" />
                                                        ) : cellLocked && !isFillerMode && !readOnly ? (
                                                            <img src={Images.lockGray} alt="" title="Read Only" className="table-cell-readonly-mark" />
                                                        ) : null;
                                                        const errorClass = missingCells.has(perCellKey) ? 'table-cell-error' : '';
                                                        // A red multi-choice cell needing more than one pick says why on hover.
                                                        const hintType = (table.cellTypes || {})[perCellKey] || col.cellType || CELL_TYPES.TEXT;
                                                        const isMultiChoiceCell =
                                                            hintType === CELL_TYPES.CHECKBOX_GROUP || hintType === CELL_TYPES.MULTI_SELECT;
                                                        const cellMinSelections = getEffectiveMinSelections(
                                                            getCellMinSelections(table, col, rIndex),
                                                            ((table.cellOptions || {})[perCellKey] || col.options || []).length
                                                        );
                                                        const errorTitle =
                                                            errorClass && isMultiChoiceCell && cellMinSelections > 1
                                                                ? `Select at least ${cellMinSelections} options`
                                                                : undefined;

                                                        // Check if this cell is split
                                                        const splitCellsMap = table.splitCells || {};
                                                        const isSplit = !!splitCellsMap[perCellKey];

                                                        const getCellValue = () => {
                                                            if (isFillerMode && watch) {
                                                                return watch(`${name}.${rIndex}.${colKey}`);
                                                            }
                                                            return rowData?.[colKey];
                                                        };

                                                        const handleCellChange = (val) => {
                                                            if (isFillerMode && setValueProp) {
                                                                setValueProp(`${name}.${rIndex}.${colKey}`, val);
                                                            } else {
                                                                handleCellChangeBuilder(rIndex, colKey, val);
                                                            }
                                                        };

                                                        if (isSplit) {
                                                            const cellVal = getCellValue();
                                                            const leftVal =
                                                                typeof cellVal === 'object' && cellVal !== null
                                                                    ? cellVal.left || ''
                                                                    : cellVal || '';
                                                            const rightVal =
                                                                typeof cellVal === 'object' && cellVal !== null ? cellVal.right || '' : '';
                                                            const cellTypesMap = table.cellTypes || {};
                                                            const cellOptionsMap = table.cellOptions || {};
                                                            const leftTypeKey = `${rIndex}_${col.id}_left`;
                                                            const rightTypeKey = `${rIndex}_${col.id}_right`;
                                                            const leftCellType = cellTypesMap[leftTypeKey] || cellType;
                                                            const rightCellType = cellTypesMap[rightTypeKey] || cellType;
                                                            const leftOptions = cellOptionsMap[leftTypeKey] || col.options;
                                                            const rightOptions = cellOptionsMap[rightTypeKey] || col.options;
                                                            return (
                                                                <td
                                                                    key={col.id || cIndex}
                                                                    className={`table-split-cell ${isSelected ? 'table-cell-selected' : ''} ${errorClass}`}
                                                                    title={errorTitle}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleCellClick(e, rIndex, cIndex);
                                                                    }}
                                                                    style={{ padding: 0, position: 'relative', ...((table.cellStyles || {})[`${rIndex}_${col.id}`] || {}) }}
                                                                >
                                                                    {cellMark}
                                                                    <div className="table-split-cell-inner">
                                                                        <div
                                                                            className="table-split-half"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleCellClick(e, rIndex, cIndex, 'left');
                                                                            }}
                                                                            onContextMenu={(e) =>
                                                                                handleContextMenu(e, rIndex, cIndex, 'left')
                                                                            }
                                                                            style={{ position: 'relative' }}
                                                                        >
                                                                            {!isFillerMode &&
                                                                                !readOnly &&
                                                                                leftCellType !== CELL_TYPES.TEXT &&
                                                                                leftCellType !== CELL_TYPES.SELECT &&
                                                                                leftCellType !== CELL_TYPES.MULTI_SELECT && (
                                                                                    <div
                                                                                        style={{
                                                                                            position: 'absolute',
                                                                                            top: 0,
                                                                                            left: 0,
                                                                                            right: 0,
                                                                                            bottom: 0,
                                                                                            zIndex: 1,
                                                                                            cursor: 'pointer',
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                            <TableCellContent
                                                                                cellType={leftCellType}
                                                                                cellValue={leftVal}
                                                                                colOptions={leftOptions}
                                                                                optionsLayout={col.optionsLayout}
                                                                                onChange={(val) =>
                                                                                    handleCellChange({
                                                                                        ...(typeof cellVal === 'object' ? cellVal : {}),
                                                                                        left: val,
                                                                                        right: rightVal,
                                                                                    })
                                                                                }
                                                                                readOnly={cellReadOnlyProp}
                                                                                isFillerMode={isFillerMode}
                                                                                mentionOptions={currentModelEnums || []}
                                                                                onMentionInsert={handleMentionInsert}
                                                                                control={control}
                                                                            />
                                                                        </div>
                                                                        <div className="table-split-divider" />
                                                                        <div
                                                                            className="table-split-half"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleCellClick(e, rIndex, cIndex, 'right');
                                                                            }}
                                                                            onContextMenu={(e) =>
                                                                                handleContextMenu(e, rIndex, cIndex, 'right')
                                                                            }
                                                                            style={{ position: 'relative' }}
                                                                        >
                                                                            {!isFillerMode &&
                                                                                !readOnly &&
                                                                                rightCellType !== CELL_TYPES.TEXT &&
                                                                                rightCellType !== CELL_TYPES.SELECT &&
                                                                                rightCellType !== CELL_TYPES.MULTI_SELECT && (
                                                                                    <div
                                                                                        style={{
                                                                                            position: 'absolute',
                                                                                            top: 0,
                                                                                            left: 0,
                                                                                            right: 0,
                                                                                            bottom: 0,
                                                                                            zIndex: 1,
                                                                                            cursor: 'pointer',
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                            <TableCellContent
                                                                                cellType={rightCellType}
                                                                                cellValue={rightVal}
                                                                                colOptions={rightOptions}
                                                                                optionsLayout={col.optionsLayout}
                                                                                onChange={(val) =>
                                                                                    handleCellChange({
                                                                                        ...(typeof cellVal === 'object' ? cellVal : {}),
                                                                                        left: leftVal,
                                                                                        right: val,
                                                                                    })
                                                                                }
                                                                                readOnly={cellReadOnlyProp}
                                                                                isFillerMode={isFillerMode}
                                                                                mentionOptions={currentModelEnums || []}
                                                                                onMentionInsert={handleMentionInsert}
                                                                                control={control}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            );
                                                        }

                                                        return (
                                                            <td
                                                                key={col.id || cIndex}
                                                                className={`${isSelected ? 'table-cell-selected' : ''} ${merge ? 'table-cell-merged' : ''} ${errorClass}`}
                                                                title={errorTitle}
                                                                onClick={(e) => handleCellClick(e, rIndex, cIndex)}
                                                                onContextMenu={(e) => handleContextMenu(e, rIndex, cIndex)}
                                                                {...(merge ? { colSpan: merge.colSpan, rowSpan: merge.rowSpan } : {})}
                                                                style={{ position: 'relative', ...((table.cellStyles || {})[`${rIndex}_${col.id}`] || {}) }}
                                                            >
                                                                {cellMark}
                                                                {!isFillerMode && !readOnly && cellType !== CELL_TYPES.TEXT && cellType !== CELL_TYPES.SELECT && cellType !== CELL_TYPES.MULTI_SELECT && (
                                                                    <div
                                                                        className="table-cell-click-overlay"
                                                                        style={{
                                                                            position: 'absolute',
                                                                            top: 0,
                                                                            left: 0,
                                                                            right: 0,
                                                                            bottom: 0,
                                                                            zIndex: 1,
                                                                            cursor: 'pointer',
                                                                        }}
                                                                    />
                                                                )}
                                                                <TableCellContent
                                                                    cellType={cellType}
                                                                    cellValue={getCellValue()}
                                                                    colOptions={(table.cellOptions || {})[perCellKey] || col.options}
                                                                    optionsLayout={col.optionsLayout}
                                                                    onChange={handleCellChange}
                                                                    readOnly={cellReadOnlyProp}
                                                                    isFillerMode={isFillerMode}
                                                                    mentionOptions={currentModelEnums || []}
                                                                    onMentionInsert={handleMentionInsert}
                                                                    control={control}
                                                                />
                                                                {errorTitle && (
                                                                    // Same wording as a field's Minimum selections error.
                                                                    <p className="table-cell-error-text">
                                                                        {countSelections(getCellValue())
                                                                            ? `${errorTitle} (${countSelections(getCellValue())} selected)`
                                                                            : errorTitle}
                                                                    </p>
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            )}}
                                        </Draggable>
                                    ))}
                                    {droppableProvided.placeholder}
                                    {!readOnly && !isFillerMode && !rowsLocked && (
                                        <tr>
                                            <td
                                                colSpan={tableHead.length + 1}
                                                style={{ padding: 0, textAlign: 'center', background: 'rgba(249, 250, 251, 0.50)' }}
                                            >
                                                <button
                                                    type="button"
                                                    className="table-add-row-circle-btn"
                                                    onClick={handleAddRow}
                                                    title="Add Row"
                                                >
                                                    <svg
                                                        width="14"
                                                        height="14"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="#0C66E4"
                                                        strokeWidth="2.5"
                                                    >
                                                        <line x1="12" y1="5" x2="12" y2="19" />
                                                        <line x1="5" y1="12" x2="19" y2="12" />
                                                    </svg>
                                                </button>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            )}
                        </Droppable>
                    </table>
                </DragDropContext>

                {/* Add Column edge button */}
                {!readOnly && !isFillerMode && (
                    <button type="button" className="table-add-column-btn" onClick={handleAddColumn} title="Add Column">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0C66E4" strokeWidth="2.5">
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                    </button>
                )}
            </div>

            {/* Context Menu */}
            {contextMenu && (
                <TableContextMenu
                    position={{ x: contextMenu.x, y: contextMenu.y }}
                    onClose={() => setContextMenu(null)}
                    onAction={handleContextAction}
                    cellType={contextMenu.cellType}
                    canMerge={contextCanMerge}
                    isMergedCell={contextIsMergedCell}
                    canSplit={contextCanSplit}
                    selectedCells={selectedCells}
                    rowsLocked={rowsLocked}
                    cellTypeLocked={contextCellTypeLocked}
                />
            )}
        </div>
    );
};
